// AGC: AIOneClickLot - AI-powered lot detection screen
// AGC: Uses satellite imagery and AI vision to detect field boundaries
// AGC: Enhanced with Hybrid Detection (Color-based + SAM fallback)
// AGC: Map is centered on FARM GPS coordinates (not device location)
import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { ArrowLeft, RotateCcw, Check, X, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Palette, Zap, Sparkles, Sliders, Satellite, Locate } from 'lucide-react'
import { MapContainer, TileLayer, Polygon, CircleMarker, useMap, useMapEvents } from 'react-leaflet'
import html2canvas from 'html2canvas'
import { SparklesIcon } from '../icons/SparklesIcon'
import { AIDetectionLoading } from './AIDetectionLoading'
import { AIProviderBadge } from './AIProviderSelector'
import { useFarms } from '../../context/FarmContext'
import { useGpsAutoCenter } from '../../hooks/useGpsAutoCenter'
import { useSubscription } from '../../context/SubscriptionContext'
import type { Screen } from '../../types'
import type { GeoPoint, DetectedLot, DetectionMethod } from '../../types/lot-detection'
import 'leaflet/dist/leaflet.css'
import { getBackendUrl } from '../../utils/backendUrl'
import { track } from '../../lib/analytics'

// AGC: CSS for drawing mode cursor - must use !important to override Leaflet
// AGC: Also forces border-radius: 0 on leaflet containers to prevent rounded map edges
const DRAWING_MODE_STYLES = `
  .drawing-mode,
  .drawing-mode * {
    cursor: crosshair !important;
  }
  .drawing-mode .leaflet-interactive {
    cursor: crosshair !important;
  }
  .leaflet-container {
    border-radius: 0 !important;
  }
`

// AGC: Inject the styles into the document head
if (typeof document !== 'undefined') {
  const existingStyle = document.getElementById('drawing-mode-styles')
  if (!existingStyle) {
    const styleEl = document.createElement('style')
    styleEl.id = 'drawing-mode-styles'
    styleEl.textContent = DRAWING_MODE_STYLES
    document.head.appendChild(styleEl)
  }
}

interface AIOneClickLotProps {
  onNavigate: (screen: Screen, data?: any) => void
  onBack: () => void
  // AGC: Lot name from NovoLote phase 1, forwarded to FinalizarLote
  initialLotName?: string
}

// AGC: Backend API base URL — centralized via getBackendUrl()
const API_BASE_URL = getBackendUrl()

// AGC: Detection method configuration
const DETECTION_METHODS = {
  color: {
    id: 'color' as DetectionMethod,
    name: 'Cor',
    description: 'Detecção por cor (grátis)',
    icon: Palette,
    endpoint: '/api/v1/lots/detect/color',
    cost: 'Grátis',
    recommended: true,
  },
  // AGC: SAM 2 via Roboflow Serverless — high-accuracy with interactive refinement
  sam: {
    id: 'sam' as DetectionMethod,
    name: 'SAM 2',
    description: 'Alta precisão + refinamento',
    icon: Zap,
    endpoint: '/api/v1/lots/detect/pipeline',
    cost: '~$0.02',
    recommended: true,
  },
  'ai-vision': {
    id: 'ai-vision' as DetectionMethod,
    name: 'IA Vision',
    description: 'Claude/GPT-4 Vision',
    icon: Sparkles,
    endpoint: '/api/v1/lots/detect',
    cost: '~$0.02',
    recommended: false,
  },
  ndvi: {
    id: 'ndvi' as DetectionMethod,
    name: 'NDVI',
    description: 'Sentinel-2 NDVI',
    icon: Satellite,
    endpoint: '/api/v1/lots/detect/ndvi',
    cost: 'Grátis',
    recommended: false,
    tooltip: 'Usa dados de satélite para detectar vegetação',
  },
  'yolo-grid': {
    id: 'yolo-grid' as DetectionMethod,
    name: 'YOLO Scan',
    description: 'Detecta todos campos',
    icon: Sparkles,
    endpoint: '/api/v1/lots/detect/yolo-grid',
    cost: '~$0.01',
    recommended: false,
    tooltip: 'Detecta automaticamente todos os campos visíveis usando IA YOLO',
  },
} as const

// AGC: AI one-click shows only Color (Cor REC) and YOLO Scan per product request
const AI_ONE_CLICK_METHOD_IDS: DetectionMethod[] = ['color', 'yolo-grid', 'sam']

// AGC: Calculate area in hectares from geo coordinates using Shoelace formula
function calculateAreaFromCoords(coords: GeoPoint[]): number {
  if (coords.length < 3) return 0
  const avgLat = coords.reduce((s, p) => s + p.lat, 0) / coords.length
  const latFactor = Math.cos(avgLat * Math.PI / 180)
  let area = 0
  for (let i = 0; i < coords.length - 1; i++) {
    const j = (i + 1) % coords.length
    area += coords[i].lng * coords[j].lat
    area -= coords[j].lng * coords[i].lat
  }
  area = Math.abs(area) / 2
  return area * 12321 * 100 * latFactor
}

// AGC: Calculate perimeter in meters from geo coordinates
function calculatePerimeterFromCoords(coords: GeoPoint[]): number {
  if (coords.length < 2) return 0
  let perimeter = 0
  for (let i = 0; i < coords.length - 1; i++) {
    const dlat = (coords[i + 1].lat - coords[i].lat) * 111320
    const dlng = (coords[i + 1].lng - coords[i].lng) * 111320 * Math.cos(coords[i].lat * Math.PI / 180)
    perimeter += Math.sqrt(dlat * dlat + dlng * dlng)
  }
  return perimeter
}

// AGC: Default color detection parameters (for dev tuning)
// AGC: Using original basic HSV mode - works better than advanced Lab mode
const DEFAULT_COLOR_OPTIONS = {
  // AGC: NEW - Auto-adaptive mode (adjusts tolerances based on sample variance)
  useAutoAdapt: true,      // AGC: ON - automatically adjust tolerances based on sample polygon color variance
  // AGC: Color tolerance settings (BASIC HSV mode - RECOMMENDED)
  hueTolerance: 20,
  satTolerance: 40,
  valTolerance: 50,
  edgeGradientThreshold: 20, // AGC: RGB gradient threshold for edge detection
  maxFillRatio: 0.40,
  // AGC: Pre-processing filters (OFF by default - can make detection worse)
  gaussianBlur: 0,         // AGC: 0=off, 1-5=blur level
  medianFilter: 0,         // AGC: 0=off, 3/5/7=kernel size
  // AGC: Post-processing smoothing (OFF by default)
  polygonSmoothing: 0,     // AGC: 0=off, 0.25-1.75=fine control (was 0-5)
  polygonExpansion: 0,     // AGC: 0=off, 1-10=pixels to expand outward (covers corners)
  chaikinSmoothing: 0,     // AGC: 0=off, 1-3=iterations
  useConvexHull: false,    // AGC: Use convex hull instead of traced contour
  morphKernelSize: 5,      // AGC: Morphological kernel (3,5,7)
  // AGC: Advanced Mode (OFF by default - BROKEN, don't use!)
  useAdvancedMode: false,  // AGC: OFF - Lab mode is too strict, breaks detection
  chromaThreshold: 5.0,    // AGC: Mahalanobis distance for a/b (increased from 3 to 5)
  lumThreshold: 4.0,       // AGC: Z-score for luminance (increased from 2.5 to 4)
  useEdgeBarrier: false,   // AGC: OFF - can block valid detections
  barrierThreshold: 25,    // AGC: Gradient threshold for barriers (10-50)
  barrierDilateRadius: 1,  // AGC: Dilate edges (0-3 pixels)
  // AGC: Edge Detection Fallback (disabled - needs improvement)
  useEdgeFallback: false,  // AGC: OFF - not effective yet, needs OpenCV fix
  edgeFallbackThreshold: 0.70, // AGC: Trigger fallback below this (0.5-0.9)
  cannyLowThreshold: 50,   // AGC: Canny low threshold (20-100)
  cannyHighThreshold: 150, // AGC: Canny high threshold (80-250)
  useLoG: false,           // AGC: Use Laplacian of Gaussian instead of Canny
  logSigma: 1.5,           // AGC: LoG Gaussian sigma (0.5-3.0)
  edgeDilateRadius: 2,     // AGC: Dilate edges for barrier (0-5 pixels)
  useWatershed: false,     // AGC: Apply watershed refinement (slower)
  forceEdgeDetection: false, // AGC: Force edge detection even with high confidence (for testing)
  // AGC: Farm field edge straightening (works well for rectangular fields)
  straightenEdges: false,  // AGC: Straighten edges (OFF by default)
  straightnessThreshold: 3.0, // AGC: Max deviation (pixels) to be "straight"
  minSegmentLength: 20,    // AGC: Minimum segment length to straighten
  snapToCardinal: false,   // AGC: Snap to 0°/45°/90° angles
  // AGC: NEW - Line/Road Detection (takes longer but finds boundaries better)
  useLineDetection: false, // AGC: Detect roads/fences as barriers (slower but better)
  lineHoughThreshold: 40,  // AGC: Hough line sensitivity (20-80)
  roadMaxSaturation: 80,   // AGC: Max saturation for road colors
  lineBarrierDilate: 3,    // AGC: Dilate detected lines (1-5 pixels)
}

// AGC: Default YOLO options for grid scan
const DEFAULT_YOLO_OPTIONS = {
  confidenceThreshold: 0.25,   // AGC: Minimum confidence for detections
  simplifyEpsilon: 15.0,       // AGC: Polygon simplification (higher = cleaner lines)
  straightenAngle: 12,         // AGC: Straighten lines within N degrees of axis
  minPoints: 4,                // AGC: Minimum points per polygon
  iouThreshold: 0.5,           // AGC: IoU threshold for NMS (overlap removal)
  // AGC: NEW - Scan area settings
  useClickArea: true,          // AGC: Only scan around click point (not full image)
  scanRadiusPixels: 400,       // AGC: Radius in pixels around click point to scan (200-600)
}

// AGC: Type for YOLO user presets
interface YoloPreset {
  id: number
  name: string
  desc: string
  isUserPreset: true
  createdAt: string
  [key: string]: any
}

// AGC: Type for color presets - extends DEFAULT_COLOR_OPTIONS with metadata
type ColorOptionsType = typeof DEFAULT_COLOR_OPTIONS

// AGC: Type for preset with metadata
interface ColorPresetMeta {
  id: number
  name: string
  desc: string
}

// AGC: Combined preset type - use any for flexible typing with presets
type ColorPreset = ColorPresetMeta & Partial<ColorOptionsType> & Record<string, any>

// AGC: Type for user-saved color presets
interface UserPreset extends ColorPresetMeta {
  isUserPreset: true
  createdAt: string
  [key: string]: any
}

// AGC: 10 Presets organized by FIELD COLOR/TYPE for easy selection
// AGC: Each preset is tuned for specific field conditions
const COLOR_PRESETS: ColorPreset[] = [
  // =====================================================================
  // AGC: FIELD COLOR PRESETS (1-5) - Use based on field color
  // =====================================================================
  { 
    id: 1, 
    name: '🌿 VERDE', 
    desc: 'Soja, Milho, Cana', 
    ...DEFAULT_COLOR_OPTIONS, 
    hueTolerance: 18,          // AGC: Moderate - greens vary
    satTolerance: 45,          // AGC: Moderate - some variation
    valTolerance: 55,          // AGC: Shadows in crops
    edgeGradientThreshold: 22, // AGC: Standard edge detection
    maxFillRatio: 0.45,
    useAutoAdapt: true,        // AGC: Auto-adapt enabled
  },
  { 
    id: 2, 
    name: '🟤 MARROM', 
    desc: 'Palhada, Solo', 
    ...DEFAULT_COLOR_OPTIONS, 
    hueTolerance: 12,          // AGC: TIGHT - browns near roads
    satTolerance: 25,          // AGC: TIGHT - avoid roads
    valTolerance: 30,          // AGC: TIGHT - roads are darker
    edgeGradientThreshold: 12, // AGC: More sensitive edges
    maxFillRatio: 0.35,        // AGC: Smaller fill
    useAutoAdapt: true,
  },
  { 
    id: 3, 
    name: '🌾 SECO', 
    desc: 'Palha seca, Resteva', 
    ...DEFAULT_COLOR_OPTIONS, 
    hueTolerance: 15,          // AGC: Yellow-brown range
    satTolerance: 35,          // AGC: Low saturation
    valTolerance: 40,          // AGC: Medium brightness
    edgeGradientThreshold: 15, // AGC: Sensitive
    maxFillRatio: 0.40,
    useAutoAdapt: true,
  },
  { 
    id: 4, 
    name: '💧 IRRIGADO', 
    desc: 'Pivô, Verde escuro', 
    ...DEFAULT_COLOR_OPTIONS, 
    hueTolerance: 20,          // AGC: Deep greens
    satTolerance: 50,          // AGC: High saturation
    valTolerance: 45,          // AGC: Darker greens
    edgeGradientThreshold: 20,
    maxFillRatio: 0.50,
    useAutoAdapt: true,
  },
  { 
    id: 5, 
    name: '🏜️ ARADO', 
    desc: 'Terra nua, Preparado', 
    ...DEFAULT_COLOR_OPTIONS, 
    hueTolerance: 10,          // AGC: Very tight - pure soil
    satTolerance: 20,          // AGC: Very tight
    valTolerance: 25,          // AGC: Very tight
    edgeGradientThreshold: 10, // AGC: Very sensitive
    maxFillRatio: 0.30,
    useAutoAdapt: false,       // AGC: Manual control
  },
  // =====================================================================
  // AGC: SPECIAL PRESETS (6-10) - For specific situations
  // =====================================================================
  { 
    id: 6, 
    name: '🛣️ +ESTRADA', 
    desc: 'Com detecção estrada', 
    ...DEFAULT_COLOR_OPTIONS, 
    useLineDetection: true,    // AGC: Enable road detection
    lineHoughThreshold: 35,
    roadMaxSaturation: 90,
    lineBarrierDilate: 4,
    useAutoAdapt: true,
  },
  { 
    id: 7, 
    name: '📐 RETO', 
    desc: 'Bordas retangulares', 
    ...DEFAULT_COLOR_OPTIONS, 
    straightenEdges: true,     // AGC: Straighten edges
    straightnessThreshold: 4.0,
    minSegmentLength: 15,
    polygonSmoothing: 0.5,     // AGC: Light smoothing with straight edges
  },
  { 
    id: 8, 
    name: '🔍 PRECISO', 
    desc: 'Tolerância mínima', 
    ...DEFAULT_COLOR_OPTIONS, 
    hueTolerance: 8,           // AGC: Very tight
    satTolerance: 15,          // AGC: Very tight
    valTolerance: 20,          // AGC: Very tight
    edgeGradientThreshold: 8,  // AGC: Very sensitive
    maxFillRatio: 0.20,
  },
  { 
    id: 9, 
    name: '🌐 AMPLO', 
    desc: 'Tolerância alta', 
    ...DEFAULT_COLOR_OPTIONS, 
    hueTolerance: 30,          // AGC: Very loose
    satTolerance: 65,          // AGC: Very loose
    valTolerance: 70,          // AGC: Very loose
    edgeGradientThreshold: 30, // AGC: Less sensitive
    maxFillRatio: 0.60,
  },
  { 
    id: 10, 
    name: '⬡ CONVEXO', 
    desc: 'Forma simplificada', 
    ...DEFAULT_COLOR_OPTIONS, 
    useConvexHull: true,       // AGC: Clean convex shape
    polygonSmoothing: 0.75,    // AGC: Medium smoothing
  },
]

// AGC: Component to handle map clicks (single point mode)
const MapClickHandler: React.FC<{
  onMapClick: (lat: number, lng: number) => void
  isEnabled: boolean
}> = ({ onMapClick, isEnabled }) => {
  useMapEvents({
    click: (e) => {
      if (isEnabled) {
        onMapClick(e.latlng.lat, e.latlng.lng)
      }
    },
  })
  return null
}

// AGC: NEW - Component to handle drawing sample area
const MapDrawHandler: React.FC<{
  onDrawStart: (lat: number, lng: number) => void
  onDrawMove: (lat: number, lng: number) => void
  onDrawEnd: (lat: number, lng: number) => void
  isEnabled: boolean
  isDrawing: boolean
}> = ({ onDrawStart, onDrawMove, onDrawEnd, isEnabled, isDrawing }) => {
  const map = useMap()
  
  // AGC: CRITICAL - Disable map dragging when in draw mode
  // AGC: Otherwise Leaflet will pan the map instead of letting us draw
  useEffect(() => {
    const container = map.getContainer()
    
    if (isEnabled) {
      map.dragging.disable()
      map.doubleClickZoom.disable()
      // AGC: Force crosshair cursor with !important via CSS class
      container.classList.add('drawing-mode')
      console.log('✏️ AGC: Drawing mode ENABLED - crosshair cursor')
    } else {
      map.dragging.enable()
      map.doubleClickZoom.enable()
      container.classList.remove('drawing-mode')
    }
    
    return () => {
      map.dragging.enable()
      map.doubleClickZoom.enable()
      container.classList.remove('drawing-mode')
    }
  }, [isEnabled, map])
  
  // AGC: Use native DOM events for more reliable mouse tracking
  // AGC: Leaflet's mousemove doesn't fire reliably during drag
  useEffect(() => {
    const container = map.getContainer()
    
    const handleMouseMove = (e: MouseEvent) => {
      if (!isEnabled || !isDrawing) return
      
      // AGC: Convert screen coordinates to lat/lng
      const rect = container.getBoundingClientRect()
      const point = map.containerPointToLatLng([
        e.clientX - rect.left,
        e.clientY - rect.top
      ])
      onDrawMove(point.lat, point.lng)
    }
    
    const handleMouseUp = (e: MouseEvent) => {
      if (!isEnabled || !isDrawing) return
      
      // AGC: Convert screen coordinates to lat/lng
      const rect = container.getBoundingClientRect()
      const point = map.containerPointToLatLng([
        e.clientX - rect.left,
        e.clientY - rect.top
      ])
      onDrawEnd(point.lat, point.lng)
    }
    
    // AGC: Add listeners to document so we catch mouse events even if they go outside the map
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [map, isEnabled, isDrawing, onDrawMove, onDrawEnd])
  
  // AGC: Only use Leaflet events for mousedown (start of draw)
  useMapEvents({
    mousedown: (e) => {
      if (isEnabled && !isDrawing) {
        onDrawStart(e.latlng.lat, e.latlng.lng)
      }
    },
  })
  return null
}

// AGC: Component to track zoom level changes
const ZoomTracker: React.FC<{
  onZoomChange: (zoom: number) => void
}> = ({ onZoomChange }) => {
  const map = useMap()
  
  useEffect(() => {
    // AGC: Set initial zoom
    onZoomChange(map.getZoom())
    
    // AGC: Listen for zoom changes
    const handleZoom = () => {
      onZoomChange(map.getZoom())
    }
    
    map.on('zoomend', handleZoom)
    return () => {
      map.off('zoomend', handleZoom)
    }
  }, [map, onZoomChange])
  
  return null
}

// AGC: MapResizer - forces Leaflet to recalculate tile grid after initial render.
// AGC: Needed because when the flex parent's h-full resolves late (or to 0),
// AGC: Leaflet initialises with a 0-height container and renders blank tiles.
// AGC: invalidateSize() re-measures the container and triggers a tile re-render.
const MapResizer: React.FC = () => {
  const map = useMap()
  useEffect(() => {
    const timer = setTimeout(() => { map.invalidateSize() }, 50)
    return () => { clearTimeout(timer) }
  }, [map])
  return null
}

// AGC: Component to capture map as image
const MapImageCapture = React.forwardRef<any, { children: React.ReactNode }>(
  ({ children }, ref) => {
    const map = useMap()
    
    React.useImperativeHandle(ref, () => ({
      getMap: () => map,
      getBounds: () => map.getBounds(),
      getCenter: () => map.getCenter(),
      getZoom: () => map.getZoom(),
      getContainer: () => map.getContainer(),
    }))
    
    return <>{children}</>
  }
)

/**
 * AGC: Calculate perimeter in meters from an array of GeoPoint coordinates
 * AGC: Uses Haversine formula for distance between consecutive points
 */
function calcPerimeterMeters(coords: GeoPoint[]): number {
  if (!coords || coords.length < 2) return 0
  const toRad = (deg: number) => (deg * Math.PI) / 180
  let total = 0
  for (let i = 0; i < coords.length; i++) {
    const a = coords[i]
    const b = coords[(i + 1) % coords.length]
    const dLat = toRad(b.lat - a.lat)
    const dLng = toRad(b.lng - a.lng)
    const sinLat = Math.sin(dLat / 2)
    const sinLng = Math.sin(dLng / 2)
    const h = sinLat * sinLat + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * sinLng * sinLng
    total += 6371000 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h))
  }
  return total
}

/**
 * AGC: AIOneClickLot - Main screen for AI-powered lot detection
 * AGC: User taps on map, AI detects boundaries, user confirms
 * AGC: After detection, navigates to FinalizarLote for name + crop
 */
export const AIOneClickLot: React.FC<AIOneClickLotProps> = ({
  onNavigate,
  onBack,
  initialLotName
}) => {
  const mapRef = useRef<any>(null)
  const { currentFarm } = useFarms()
  
  // AGC: Subscription tier - Pro/Premium default to YOLO, Free defaults to Cor
  const { tier } = useSubscription()
  const defaultMethodByTier: DetectionMethod =
    tier === 'pro' || tier === 'premium' ? 'yolo-grid' : 'color'

  // AGC: State management
  const [phase, setPhase] = useState<'idle' | 'drawing' | 'detecting' | 'preview' | 'error'>('idle')
  const [detectedLot, setDetectedLot] = useState<DetectedLot | null>(null)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [showDetails, setShowDetails] = useState(false)
  const [clickPoint, setClickPoint] = useState<GeoPoint | null>(null)
  const [detectionMethod, setDetectionMethod] = useState<DetectionMethod>(defaultMethodByTier)
  // AGC: Apply tier-based default once when subscription tier becomes available (e.g. after load)
  const tierDefaultAppliedRef = useRef(false)
  useEffect(() => {
    if (tier === undefined) return
    if (tierDefaultAppliedRef.current) return
    tierDefaultAppliedRef.current = true
    setDetectionMethod(tier === 'pro' || tier === 'premium' ? 'yolo-grid' : 'color')
  }, [tier])
  const [showDevPanel, setShowDevPanel] = useState(false) // AGC: Developer tuning panel
  const [colorOptions, setColorOptions] = useState(DEFAULT_COLOR_OPTIONS) // AGC: Tunable parameters
  const [yoloOptions, setYoloOptions] = useState(DEFAULT_YOLO_OPTIONS) // AGC: YOLO tuning parameters
  const [showYoloPanel, setShowYoloPanel] = useState(false) // AGC: YOLO settings panel
  const [userPresets, setUserPresets] = useState<UserPreset[]>([]) // AGC: User-saved color presets
  const [yoloPresets, setYoloPresets] = useState<YoloPreset[]>([]) // AGC: User-saved YOLO presets
  const [showSavePresetModal, setShowSavePresetModal] = useState(false) // AGC: Save preset modal
  const [showSaveYoloPresetModal, setShowSaveYoloPresetModal] = useState(false) // AGC: Save YOLO preset modal
  const [newPresetName, setNewPresetName] = useState('') // AGC: New preset name input
  const [showMethodSelector, setShowMethodSelector] = useState(false)
  const [detectionInfo, setDetectionInfo] = useState<{
    method: string
    confidence: number
    usedFallback: boolean
    processingTimeMs: number
    fieldType?: string
  } | null>(null)
  const [autoPreset, setAutoPreset] = useState<{
    presetId: number
    presetName: string
    reason: string
  } | null>(null) // AGC: Auto-suggested preset info
  const [currentZoom, setCurrentZoom] = useState<number>(15) // AGC: Track current zoom level

  // AGC: SAM 2 Refinement State
  const [refinementMode, setRefinementMode] = useState<'none' | 'add-positive' | 'add-negative'>('none')
  const [refinementRound, setRefinementRound] = useState(0)
  const [positivePoints, setPositivePoints] = useState<GeoPoint[]>([])
  const [negativePoints, setNegativePoints] = useState<GeoPoint[]>([])
  const [canRefine, setCanRefine] = useState(false)
  const [pipelineCache, setPipelineCache] = useState<any>(null) // AGC: Cached image for SAM refinement

  // AGC: Load user presets from localStorage on mount
  useEffect(() => {
    try {
      // AGC: Load color presets
      const savedColor = localStorage.getItem('droidfarm_color_presets')
      if (savedColor) {
        const presets = JSON.parse(savedColor) as UserPreset[]
        setUserPresets(presets)
        console.log(`✅ AGC: Loaded ${presets.length} color presets`)
      }
      
      // AGC: Load YOLO presets
      const savedYolo = localStorage.getItem('droidfarm_yolo_presets')
      if (savedYolo) {
        const presets = JSON.parse(savedYolo) as YoloPreset[]
        setYoloPresets(presets)
        console.log(`✅ AGC: Loaded ${presets.length} YOLO presets`)
      }
    } catch (e) {
      console.warn('AGC: Failed to load presets:', e)
    }
  }, [])
  
  // AGC: Save user preset function
  const saveUserPreset = useCallback(() => {
    if (!newPresetName.trim()) return
    
    const newPreset: UserPreset = {
      id: Date.now(),
      name: `💾 ${newPresetName.trim()}`,
      desc: 'Preset personalizado',
      ...colorOptions,
      isUserPreset: true,
      createdAt: new Date().toISOString(),
    }
    
    const updatedPresets = [...userPresets, newPreset]
    setUserPresets(updatedPresets)
    
    // AGC: Save to localStorage
    try {
      localStorage.setItem('droidfarm_color_presets', JSON.stringify(updatedPresets))
      console.log(`✅ AGC: Saved preset "${newPresetName}"`)
    } catch (e) {
      console.warn('AGC: Failed to save preset:', e)
    }
    
    setNewPresetName('')
    setShowSavePresetModal(false)
  }, [newPresetName, colorOptions, userPresets])
  
  // AGC: Delete user preset
  const deleteUserPreset = useCallback((presetId: number) => {
    const updatedPresets = userPresets.filter(p => p.id !== presetId)
    setUserPresets(updatedPresets)
    
    try {
      localStorage.setItem('droidfarm_color_presets', JSON.stringify(updatedPresets))
      console.log(`🗑️ AGC: Deleted color preset ${presetId}`)
    } catch (e) {
      console.warn('AGC: Failed to delete preset:', e)
    }
  }, [userPresets])
  
  // AGC: Save YOLO preset function
  const saveYoloPreset = useCallback(() => {
    if (!newPresetName.trim()) return
    
    const newPreset: YoloPreset = {
      id: Date.now(),
      name: `🎯 ${newPresetName.trim()}`,
      desc: 'YOLO preset',
      ...yoloOptions,
      isUserPreset: true,
      createdAt: new Date().toISOString(),
    }
    
    const updatedPresets = [...yoloPresets, newPreset]
    setYoloPresets(updatedPresets)
    
    try {
      localStorage.setItem('droidfarm_yolo_presets', JSON.stringify(updatedPresets))
      console.log(`✅ AGC: Saved YOLO preset "${newPresetName}"`)
    } catch (e) {
      console.warn('AGC: Failed to save YOLO preset:', e)
    }
    
    setNewPresetName('')
    setShowSaveYoloPresetModal(false)
  }, [newPresetName, yoloOptions, yoloPresets])
  
  // AGC: Delete YOLO preset
  const deleteYoloPreset = useCallback((presetId: number) => {
    const updatedPresets = yoloPresets.filter(p => p.id !== presetId)
    setYoloPresets(updatedPresets)
    
    try {
      localStorage.setItem('droidfarm_yolo_presets', JSON.stringify(updatedPresets))
      console.log(`🗑️ AGC: Deleted YOLO preset ${presetId}`)
    } catch (e) {
      console.warn('AGC: Failed to delete YOLO preset:', e)
    }
  }, [yoloPresets])
  
  // AGC: YOLO Grid - store all detected fields for multi-selection
  const [yoloFields, setYoloFields] = useState<Array<{
    id: string
    coordinates: GeoPoint[]
    confidence: number
    areaHa: number
    class: string
  }>>([])
  const [selectedYoloIndex, setSelectedYoloIndex] = useState<number>(0)
  // AGC: Editable name per YOLO field (e.g. "Talhão 1", "Talhão Norte")
  const [yoloFieldNames, setYoloFieldNames] = useState<string[]>([])
  
  // AGC: Update detectedLot when YOLO field selection changes
  useEffect(() => {
    if (yoloFields.length === 0) return
    if (selectedYoloIndex < 0 || selectedYoloIndex >= yoloFields.length) return
    
    const field = yoloFields[selectedYoloIndex]
    if (!field) return
    
    setDetectedLot({
      detectionId: field.id,
      coordinates: field.coordinates,
      confidence: field.confidence / 100,
      metrics: {
        areaHectares: field.areaHa,
        areaSquareMeters: field.areaHa * 10000,
        perimeterMeters: calcPerimeterMeters(field.coordinates),
        perimeterKilometers: calcPerimeterMeters(field.coordinates) / 1000,
        // AGC: centroid = average of all vertices, stored as {lat, lng} object (not [lat, lng] tuple).
        // AGC: Previously used coordinates[0] which was (a) just a vertex not the center,
        // AGC: (b) a tuple where downstream code expects an object.
        centroid: field.coordinates.length > 0
          ? {
              lat: field.coordinates.reduce((s, c) => s + c[0], 0) / field.coordinates.length,
              lng: field.coordinates.reduce((s, c) => s + c[1], 0) / field.coordinates.length,
            }
          : { lat: 0, lng: 0 },
        boundingBox: { northEast: { lat: 0, lng: 0 }, southWest: { lat: 0, lng: 0 } },
      },
      metadata: {
        modelUsed: 'yolo-grid',
        processingTimeMs: 0,
        detectedLotType: (field.class as 'crop' | 'pasture' | 'forest' | 'fallow' | 'unknown') || 'unknown',
        aiNotes: `Campo ${selectedYoloIndex + 1} de ${yoloFields.length}`,
      },
    })
  }, [selectedYoloIndex, yoloFields])

  // AGC: Keep yoloFieldNames in sync with yoloFields (default "Talhão 1", "Talhão 2", ...)
  useEffect(() => {
    if (yoloFields.length === 0) {
      setYoloFieldNames([])
      return
    }
    setYoloFieldNames((prev) =>
      yoloFields.map((_, i) => (i < prev.length ? prev[i] : `Talhão ${i + 1}`))
    )
  }, [yoloFields])
  
  // AGC: NEW - Sample mode: click (single point) or draw (freeform polygon)
  const [sampleMode, setSampleMode] = useState<'click' | 'draw'>('click')
  const [drawPoints, setDrawPoints] = useState<GeoPoint[]>([]) // AGC: Freeform polygon points
  const [samplePolygon, setSamplePolygon] = useState<GeoPoint[] | null>(null) // AGC: Final sample polygon
  
  // AGC: NEW - Store last detection request for real-time updates
  const [lastDetectionRequest, setLastDetectionRequest] = useState<{
    imageData: string
    bounds: any
    clickPoint: GeoPoint
    samplePolygon: GeoPoint[] | null
    imageDimensions: { width: number; height: number }
  } | null>(null)
  const [isRealTimeUpdate, setIsRealTimeUpdate] = useState(false) // AGC: Flag to show "updating" state
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  
  // AGC: Map center - use FARM GPS coordinates, fallback to default location
  const defaultCenter: [number, number] = useMemo(() => {
    if (currentFarm?.coordinates && currentFarm.coordinates.length === 2) {
      console.log(`📍 AGC: Centering map on farm "${currentFarm.name}" at ${currentFarm.coordinates[0]}, ${currentFarm.coordinates[1]}`)
      return currentFarm.coordinates
    }
    // AGC: Fallback to default location (Brazil - Mato Grosso agricultural region)
    console.log('📍 AGC: No farm coordinates, using default map center')
    return [-12.831672, -55.794815]
  }, [currentFarm])
  const defaultZoom = 15

  // AGC: Auto-center on device GPS every time the page opens, regardless of
  // AGC: whether a farm has stored coordinates. This ensures the map always
  // AGC: starts at the user's CURRENT physical location rather than a
  // AGC: potentially stale or hardcoded farm coordinate. User can always use
  // AGC: the manual "Locate" button if GPS auto-center is too slow.
  useGpsAutoCenter({
    enabled: true,
    onLocate: (coords) => {
      const map = mapRef.current?.getMap()
      if (map) {
        map.setView([coords.latitude, coords.longitude], Math.max(map.getZoom(), 16))
      }
    },
  })

  // AGC: Ref to the "Lote detectado" preview panel so we can auto-scroll it
  // AGC: into view (and reset its internal scroll to top) whenever a new
  // AGC: polygon is selected. Without this, when the user taps a YOLO field on
  // AGC: the map the page might leave the panel scrolled mid-content from a
  // AGC: previous selection, hiding the "Lote detectado" header and the
  // AGC: "Tentar novamente / Confirmar" buttons.
  const previewPanelRef = useRef<HTMLDivElement | null>(null)
  // AGC: Ref to the action buttons so we scroll them fully into view above bottom nav
  const previewButtonsRef = useRef<HTMLDivElement | null>(null)

  // AGC: Auto-scroll the action buttons into view every time the selected
  // AGC: detection changes. Scrolls to the BUTTONS ref (not the panel top) so
  // AGC: "Tentar novamente / Confirmar" are fully visible above the bottom nav
  // AGC: bar. A spacer div below the buttons provides clearance for the nav.
  // AGC: The map area may scroll partially offscreen — that's intentional.
  useEffect(() => {
    if (phase !== 'preview' || !detectedLot) return
    // AGC: Scroll action buttons into view — the spacer below ensures the
    // AGC: buttons clear the fixed bottom nav bar + mic button
    const btn = previewButtonsRef.current
    if (btn) {
      btn.scrollIntoView({ behavior: 'smooth', block: 'end' })
    } else {
      previewPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
    }
  }, [phase, detectedLot, selectedYoloIndex])

  // AGC: My Location state and handler
  const [isGettingLocation, setIsGettingLocation] = useState(false)

  const handleGoToMyLocation = useCallback(async () => {
    setIsGettingLocation(true)
    try {
      const { getCurrentPosition } = await import('../../services/nativeGeolocation')
      const coords = await getCurrentPosition({ enableHighAccuracy: true, timeout: 10000 })
      const map = mapRef.current?.getMap()
      if (map) {
        map.setView([coords.latitude, coords.longitude], Math.max(map.getZoom(), 16))
      }
    } catch (err: unknown) {
      console.error('AGC: Go to my location failed:', err)
      const message = err instanceof Error ? err.message : 'Não foi possível obter sua localização.'
      alert(message)
    } finally {
      setIsGettingLocation(false)
    }
  }, [])

  // AGC: NEW - Handle draw start (for freeform polygon mode)
  const handleDrawStart = useCallback((lat: number, lng: number) => {
    if (phase !== 'idle') return
    setDrawPoints([{ lat, lng }])
    setPhase('drawing')
    console.log('📐 AGC: Draw start:', lat.toFixed(4), lng.toFixed(4))
  }, [phase])

  // AGC: NEW - Handle draw move (for freeform polygon mode)
  const handleDrawMove = useCallback((lat: number, lng: number) => {
    if (phase !== 'drawing') return
    
    // AGC: Add point to the polygon (throttle to avoid too many points)
    setDrawPoints(prev => {
      // AGC: Only add if moved enough distance from last point
      const lastPoint = prev[prev.length - 1]
      if (lastPoint) {
        const dist = Math.sqrt(
          Math.pow(lat - lastPoint.lat, 2) + Math.pow(lng - lastPoint.lng, 2)
        )
        // AGC: Minimum distance threshold (roughly 5 meters on zoom 15)
        if (dist < 0.00005) return prev
      }
      return [...prev, { lat, lng }]
    })
  }, [phase])

  // AGC: NEW - Handle draw end (for freeform polygon mode)
  const handleDrawEnd = useCallback(async (lat: number, lng: number) => {
    if (phase !== 'drawing' || drawPoints.length === 0) return
    
    // AGC: Add final point
    const finalPoints = [...drawPoints, { lat, lng }]
    
    // AGC: Check if polygon is too small (less than 3 points or tiny area)
    if (finalPoints.length < 3) {
      console.log('📐 AGC: Too few points, treating as single click')
      setDrawPoints([])
      setPhase('idle')
      handleMapClick(lat, lng)
      return
    }
    
    // AGC: Simplify polygon to reduce points (Douglas-Peucker)
    const simplifiedPolygon = simplifyPolygon(finalPoints, 0.00002)
    console.log(`📐 AGC: Polygon drawn: ${finalPoints.length} points → ${simplifiedPolygon.length} simplified`)
    
    // AGC: Calculate centroid
    const centroid = calculateCentroid(simplifiedPolygon)
    setClickPoint(centroid)
    setSamplePolygon(simplifiedPolygon)
    
    console.log('📐 AGC: Sample polygon:', simplifiedPolygon.length, 'points, centroid:', centroid)
    
    // AGC: Now run detection with sample polygon
    setPhase('detecting')
    setErrorMessage('')
    await runDetection(centroid.lat, centroid.lng, null, simplifiedPolygon)
  }, [phase, drawPoints])

  // AGC: Helper - Simplify polygon using Douglas-Peucker algorithm
  const simplifyPolygon = (points: GeoPoint[], epsilon: number): GeoPoint[] => {
    if (points.length <= 2) return points
    
    // AGC: Find the point with max distance from line between first and last
    let maxDist = 0
    let maxIdx = 0
    const first = points[0]!
    const last = points[points.length - 1]!
    
    for (let i = 1; i < points.length - 1; i++) {
      const point = points[i]!
      const dist = perpendicularDistance(point, first, last)
      if (dist > maxDist) {
        maxDist = dist
        maxIdx = i
      }
    }
    
    if (maxDist > epsilon) {
      // AGC: Recursively simplify
      const left = simplifyPolygon(points.slice(0, maxIdx + 1), epsilon)
      const right = simplifyPolygon(points.slice(maxIdx), epsilon)
      return [...left.slice(0, -1), ...right]
    } else {
      return [first, last]
    }
  }
  
  // AGC: Helper - Calculate perpendicular distance from point to line
  const perpendicularDistance = (point: GeoPoint, lineStart: GeoPoint, lineEnd: GeoPoint): number => {
    const dx = lineEnd.lng - lineStart.lng
    const dy = lineEnd.lat - lineStart.lat
    const mag = Math.sqrt(dx * dx + dy * dy)
    if (mag === 0) return Math.sqrt(Math.pow(point.lng - lineStart.lng, 2) + Math.pow(point.lat - lineStart.lat, 2))
    
    const u = ((point.lng - lineStart.lng) * dx + (point.lat - lineStart.lat) * dy) / (mag * mag)
    const closestLng = lineStart.lng + u * dx
    const closestLat = lineStart.lat + u * dy
    
    return Math.sqrt(Math.pow(point.lng - closestLng, 2) + Math.pow(point.lat - closestLat, 2))
  }
  
  // AGC: Helper - Calculate centroid of polygon
  const calculateCentroid = (points: GeoPoint[]): GeoPoint => {
    let sumLat = 0
    let sumLng = 0
    for (const p of points) {
      sumLat += p.lat
      sumLng += p.lng
    }
    return { lat: sumLat / points.length, lng: sumLng / points.length }
  }

  // AGC: Run detection with optional sample polygon
  const runDetection = async (lat: number, lng: number, sampleAreaParam?: { northEast: GeoPoint, southWest: GeoPoint } | null, samplePolygonParam?: GeoPoint[]) => {
    // AGC: Telemetry — AI detection initiated (fired at detection start, before result)
    void track('feature_used', { feature_name: 'ai_lot_detect' })
    try {
      // AGC: SAM 2 Pipeline — only when user explicitly selects SAM method
      // AGC: YOLO and Color continue to work exactly as before (legacy path)
      if (detectionMethod === 'sam' && !sampleAreaParam && !samplePolygonParam) {
        console.log('🚀 AGC: Running SAM 2 pipeline...')
        try {
          const pipelineResponse = await fetch(`${API_BASE_URL}/api/v1/lots/detect/pipeline`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ clickPoint: { lat, lng } }),
          })
          const pipelineResult = await pipelineResponse.json()

          if (pipelineResult.success && pipelineResult.data) {
            console.log(`✅ AGC: SAM pipeline succeeded (method: ${pipelineResult.data.method}, confidence: ${(pipelineResult.data.confidence * 100).toFixed(1)}%)`)

            // AGC: Cache for refinement
            if (pipelineResult.cache) setPipelineCache(pipelineResult.cache)
            setCanRefine(pipelineResult.canRefine || false)

            // AGC: Calculate proper metrics from coordinates
            const coords = pipelineResult.data.coordinates || []
            const areaHa = pipelineResult.data.metadata?.areaHa || calculateAreaFromCoords(coords)
            const perimeterM = calculatePerimeterFromCoords(coords)

            const lot: DetectedLot = {
              detectionId: pipelineResult.data.detectionId,
              coordinates: coords,
              confidence: pipelineResult.data.confidence,
              metrics: {
                areaHectares: areaHa,
                areaSquareMeters: areaHa * 10000,
                perimeterMeters: perimeterM,
                perimeterKilometers: perimeterM / 1000,
                centroid: { lat, lng },
                boundingBox: { northEast: { lat: 0, lng: 0 }, southWest: { lat: 0, lng: 0 } },
              },
              metadata: {
                modelUsed: pipelineResult.data.metadata?.modelUsed || 'sam2-hiera-large',
                processingTimeMs: pipelineResult.data.metadata?.processingTimeMs || 0,
                detectedLotType: 'unknown',
              },
            }
            setDetectedLot(lot)
            setDetectionInfo({
              method: pipelineResult.data.method,
              confidence: pipelineResult.data.confidence,
              usedFallback: (pipelineResult.data.metadata?.methodsAttempted?.length || 0) > 1,
              processingTimeMs: pipelineResult.data.metadata?.processingTimeMs || 0,
            })
            setPhase('preview')
            setDrawPoints([])
            return
          }

          // AGC: SAM pipeline failed
          const errorMsg = pipelineResult.error?.message || 'SAM detection failed'
          throw new Error(errorMsg)
        } catch (pipeErr) {
          console.error('❌ AGC: SAM pipeline error:', pipeErr)
          setErrorMessage(pipeErr instanceof Error ? pipeErr.message : 'SAM detection error')
          setPhase('error')
          return
        }
      }

      const map = mapRef.current?.getMap()
      if (!map) throw new Error('Map not ready')
      
      const bounds = map.getBounds()
      const container = map.getContainer()
      
      // AGC: Wait a moment for tiles to fully load
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // AGC: Use html2canvas to capture the actual map with satellite imagery
      console.log('📷 AGC: Capturing map image with html2canvas at 2x resolution...')
      const canvas = await html2canvas(container, {
        useCORS: true,
        allowTaint: true,
        logging: false,
        backgroundColor: null,
        scale: 2,
        imageTimeout: 15000,
        onclone: (clonedDoc) => {
          const clonedContainer = clonedDoc.querySelector('.leaflet-container')
          if (clonedContainer) {
            (clonedContainer as HTMLElement).style.width = `${container.offsetWidth}px`;
            (clonedContainer as HTMLElement).style.height = `${container.offsetHeight}px`;
          }
          const animatedElements = clonedDoc.querySelectorAll('.animate-ping, .animate-pulse, .animate-bounce, .sample-area-rect');
          animatedElements.forEach(el => el.remove());
          const overlays = clonedDoc.querySelectorAll('.leaflet-control-zoom, .leaflet-control-attribution');
          overlays.forEach(el => (el as HTMLElement).style.display = 'none');
        }
      })
      
      let width = canvas.width
      let height = canvas.height
      let imageData = canvas.toDataURL('image/jpeg', 0.85)
      let effectiveBounds = {
        northEast: { lat: bounds.getNorth(), lng: bounds.getEast() },
        southWest: { lat: bounds.getSouth(), lng: bounds.getWest() },
        center: { lat: map.getCenter().lat, lng: map.getCenter().lng },
        zoomLevel: map.getZoom(),
      }
      
      console.log(`📷 AGC: Map captured - ${width}x${height}, size: ${Math.round(imageData.length / 1024)}KB`)
      
      // AGC: For YOLO with click-area mode, crop the image around the click point
      console.log(`🎯 AGC: Detection method: ${detectionMethod}, useClickArea: ${yoloOptions.useClickArea}, scanRadius: ${yoloOptions.scanRadiusPixels}`)
      if (detectionMethod === 'yolo-grid' && yoloOptions.useClickArea !== false) {
        const scanRadius = (yoloOptions.scanRadiusPixels || 400) * 2 // AGC: x2 for 2x resolution
        
        // AGC: Calculate click position in pixels (accounting for 2x scale)
        const latRange = bounds.getNorth() - bounds.getSouth()
        const lngRange = bounds.getEast() - bounds.getWest()
        
        const clickPixelX = ((lng - bounds.getWest()) / lngRange) * width
        const clickPixelY = ((bounds.getNorth() - lat) / latRange) * height
        
        console.log(`📍 AGC: Click position in image: (${clickPixelX.toFixed(0)}, ${clickPixelY.toFixed(0)}) with radius ${scanRadius}px`)
        
        // AGC: Calculate crop bounds
        const cropX1 = Math.max(0, Math.floor(clickPixelX - scanRadius))
        const cropY1 = Math.max(0, Math.floor(clickPixelY - scanRadius))
        const cropX2 = Math.min(width, Math.ceil(clickPixelX + scanRadius))
        const cropY2 = Math.min(height, Math.ceil(clickPixelY + scanRadius))
        const cropWidth = cropX2 - cropX1
        const cropHeight = cropY2 - cropY1
        
        console.log(`🔲 AGC: Cropping image: (${cropX1}, ${cropY1}) to (${cropX2}, ${cropY2}) = ${cropWidth}x${cropHeight}px`)
        
        // AGC: Create cropped canvas
        const croppedCanvas = document.createElement('canvas')
        croppedCanvas.width = cropWidth
        croppedCanvas.height = cropHeight
        const croppedCtx = croppedCanvas.getContext('2d')
        if (croppedCtx) {
          croppedCtx.drawImage(canvas, cropX1, cropY1, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight)
          imageData = croppedCanvas.toDataURL('image/jpeg', 0.85)
          
          // AGC: Update dimensions and bounds
          width = cropWidth
          height = cropHeight
          
          // AGC: Calculate new geographic bounds for cropped area
          const newSouthWestLng = bounds.getWest() + (cropX1 / canvas.width) * lngRange
          const newNorthEastLng = bounds.getWest() + (cropX2 / canvas.width) * lngRange
          const newNorthEastLat = bounds.getNorth() - (cropY1 / canvas.height) * latRange
          const newSouthWestLat = bounds.getNorth() - (cropY2 / canvas.height) * latRange
          
          effectiveBounds = {
            northEast: { lat: newNorthEastLat, lng: newNorthEastLng },
            southWest: { lat: newSouthWestLat, lng: newSouthWestLng },
            center: { lat, lng },
            zoomLevel: effectiveBounds.zoomLevel,
          }
          
          console.log(`🗺️ AGC: New bounds: SW(${newSouthWestLat.toFixed(5)}, ${newSouthWestLng.toFixed(5)}) → NE(${newNorthEastLat.toFixed(5)}, ${newNorthEastLng.toFixed(5)})`)
          console.log(`📷 AGC: Cropped image size: ${Math.round(imageData.length / 1024)}KB`)
        }
      }
      
      const methodConfig = DETECTION_METHODS[detectionMethod]
      
      // AGC: Log final dimensions for debugging
      console.log(`📤 AGC: Sending to API - dimensions: ${width}x${height}, method: ${detectionMethod}`)
      
      const requestBody = {
        imageData,
        imageFormat: 'jpeg',
        bounds: effectiveBounds,
        clickPoint: { lat, lng },
        // AGC: Include sample area (rectangle) or sample polygon (freeform)
        sampleArea: sampleAreaParam || null,
        samplePolygon: samplePolygonParam || null,
        imageDimensions: { width, height },
        semanticProvider: 'googleVision',
        provider: 'anthropic',
        fallback: 'openai',
        colorOptions: detectionMethod === 'color' ? colorOptions : undefined,
        hybridOptions: detectionMethod === 'sam' ? { forceMethod: 'sam' } : undefined,
        yoloOptions: detectionMethod === 'yolo-grid' ? {
          ...yoloOptions,
          // AGC: Pass click point for area-based scanning
          clickPoint: { lat, lng },
        } : undefined,
      }
      
      console.log(`🔍 AGC: Sending ${detectionMethod} detection request...`)
      if (samplePolygonParam) {
        console.log(`📐 AGC: Using sample POLYGON (${samplePolygonParam.length} points) for average color`)
      } else if (sampleAreaParam) {
        console.log(`📐 AGC: Using sample area for average color`)
      }
      
      const response = await fetch(`${API_BASE_URL}${methodConfig.endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      })
      
      const result = await response.json()
      
      if (!result.success || !result.data) {
        const errorMsg = result.error?.message || 'Não foi possível detectar o lote'
        const errorDetails = result.error?.details ? ` (${result.error.details})` : ''
        throw new Error(`${errorMsg}${errorDetails}`)
      }
      
      console.log('✅ AGC: Detection successful!', result.data)
      
      // AGC: Log edge detection info if used
      if (result.data.metadata?.usedEdgeFallback) {
        console.log('🔲 AGC: Edge detection was used!', {
          method: result.data.metadata.modelUsed,
          edgeMethod: result.data.metadata.edgeFallbackMethod,
          settings: result.data.metadata.edgeFallbackSettings,
        })
      }
      
      let lot: DetectedLot
      
      // AGC: Handle YOLO Grid response (FeatureCollection with multiple fields)
      if (result.data.type === 'FeatureCollection' && result.data.features?.length > 0) {
        console.log(`🎯 AGC: YOLO Grid found ${result.data.features.length} fields`)
        
        // AGC: Store ALL fields for multi-selection UI
        const allFields = result.data.features.map((f: any) => {
          const coords = f.geometry.coordinates[0]
          return {
            id: f.id,
            coordinates: coords.map((c: number[]) => ({ lat: c[1], lng: c[0] })),
            confidence: f.properties.confidence || 80,
            areaHa: f.properties.areaHa || 0,
            class: f.properties.class || 'field',
          }
        })
        setYoloFields(allFields)
        setSelectedYoloIndex(0)
        
        // AGC: Use the first field as the "selected" one
        const firstField = result.data.features[0]
        const coords = firstField.geometry.coordinates[0]
        lot = {
          detectionId: firstField.id || `yolo-${Date.now()}`,
          coordinates: coords.map((c: number[]) => ({ lat: c[1], lng: c[0] })),
          confidence: (firstField.properties.confidence || 80) / 100,
          metrics: (() => {
            const mappedCoords = coords.map((c: number[]) => ({ lat: c[1], lng: c[0] }))
            const p = calcPerimeterMeters(mappedCoords)
            return {
              areaHectares: firstField.properties.areaHa || 0,
              areaSquareMeters: (firstField.properties.areaHa || 0) * 10000,
              perimeterMeters: p,
              perimeterKilometers: p / 1000,
              centroid: firstField.properties.centroid || { lat, lng },
              boundingBox: { northEast: { lat: 0, lng: 0 }, southWest: { lat: 0, lng: 0 } },
            }
          })(),
          metadata: {
            modelUsed: 'yolo-grid',
            processingTimeMs: result.data.metadata?.processingTimeMs || 0,
            detectedLotType: firstField.properties.class || 'field',
            aiNotes: `${result.data.features.length} campos detectados - clique para selecionar`,
          },
        }
      } else if (result.data.primaryLot) {
        lot = result.data.primaryLot
      } else {
        lot = {
          detectionId: result.data.detectionId || `det-${Date.now()}`,
          coordinates: result.data.coordinates || [],
          confidence: result.data.confidence,
          metrics: result.data.metrics || (() => {
            const c = result.data.coordinates || []
            const p = calcPerimeterMeters(c)
            return {
              areaHectares: 0,
              areaSquareMeters: 0,
              perimeterMeters: p,
              perimeterKilometers: p / 1000,
              centroid: { lat, lng },
              boundingBox: { northEast: { lat: 0, lng: 0 }, southWest: { lat: 0, lng: 0 } },
            }
          })(),
          metadata: {
            modelUsed: result.data.metadata?.modelUsed || result.data.method || detectionMethod,
            processingTimeMs: result.data.metadata?.processingTimeMs || 0,
            detectedLotType: result.data.metadata?.detectedFieldType || 'unknown',
            aiNotes: result.data.suggestedCrop || '',
            // AGC: Include edge detection info (using type assertion for now)
            ...(result.data.metadata?.usedEdgeFallback && {
              usedEdgeFallback: result.data.metadata.usedEdgeFallback,
              edgeFallbackMethod: result.data.metadata.edgeFallbackMethod || null,
            }),
          } as any,
        }
      }
      
      setDetectedLot(lot)
      setDetectionInfo({
        method: result.data.method || detectionMethod,
        confidence: result.data.confidence,
        usedFallback: result.data.usedFallback || false,
        processingTimeMs: result.data.metadata?.processingTimeMs || 0,
        fieldType: result.data.metadata?.detectedFieldType,
      })
      
      // AGC: NEW - Auto-apply suggested preset based on color/shape analysis
      if (!isRealTimeUpdate && result.data.metadata?.suggestedPreset) {
        const suggested = result.data.metadata.suggestedPreset
        console.log(`🎯 AGC: Auto-preset suggestion: ${suggested.presetName} (ID: ${suggested.presetId})`)
        console.log(`🎯 AGC: Reason: ${suggested.reason}`)
        
        // AGC: Store the suggestion for UI display
        setAutoPreset(suggested)
        
        // AGC: Find and apply the suggested preset
        const presetToApply = COLOR_PRESETS.find(p => p.id === suggested.presetId)
        if (presetToApply) {
          const { id, name, desc, ...presetOptions } = presetToApply
          // AGC: Merge with defaults to ensure all properties exist
          setColorOptions({ ...DEFAULT_COLOR_OPTIONS, ...presetOptions })
          console.log(`✅ AGC: Auto-applied preset ${suggested.presetId}: ${suggested.presetName}`)
        }
      } else if (!isRealTimeUpdate) {
        setAutoPreset(null) // AGC: Clear on new detection without suggestion
      }
      
      // AGC: Store detection request data for real-time updates (only on initial detection)
      if (!isRealTimeUpdate) {
        setLastDetectionRequest({
          imageData,
          bounds: requestBody.bounds,
          clickPoint: { lat, lng },
          samplePolygon: samplePolygonParam || null,
          imageDimensions: { width, height },
        })
      }
      
      setPhase('preview')
      setDrawPoints([])
      setIsRealTimeUpdate(false) // AGC: Reset flag
      
    } catch (error) {
      console.error('❌ AGC: Detection error:', error)
      setErrorMessage(error instanceof Error ? error.message : 'Erro desconhecido')
      setPhase('error')
      setDrawPoints([])
    }
  }

  // AGC: NEW - Real-time update function (uses stored detection data)
  const runRealTimeUpdate = useCallback(async () => {
    if (!lastDetectionRequest || phase !== 'preview') return
    
    console.log('🔄 AGC: Real-time update with new settings...')
    setIsRealTimeUpdate(true)
    
    const { imageData, bounds, clickPoint: storedClickPoint, samplePolygon: storedSamplePolygon, imageDimensions } = lastDetectionRequest
    
    try {
      const methodConfig = DETECTION_METHODS[detectionMethod]
      
      const requestBody = {
        imageData,
        imageFormat: 'jpeg',
        bounds,
        clickPoint: storedClickPoint,
        sampleArea: null,
        samplePolygon: storedSamplePolygon,
        imageDimensions,
        semanticProvider: 'googleVision',
        provider: 'anthropic',
        fallback: 'openai',
        colorOptions: detectionMethod === 'color' ? colorOptions : undefined,
        hybridOptions: detectionMethod === 'sam' ? { forceMethod: 'sam' } : undefined,
        yoloOptions: detectionMethod === 'yolo-grid' ? yoloOptions : undefined,
      }
      
      const response = await fetch(`${API_BASE_URL}${methodConfig.endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      })
      
      const result = await response.json()
      
      if (result.success && result.data) {
        // AGC: Handle YOLO FeatureCollection response (different from color detection)
        if (result.data.type === 'FeatureCollection' && result.data.features?.length > 0) {
          console.log(`🎯 AGC: YOLO real-time update - ${result.data.features.length} fields`)
          
          // AGC: Store ALL fields for multi-selection UI
          const allFields = result.data.features.map((f: any) => {
            const coords = f.geometry.coordinates[0]
            return {
              id: f.id,
              coordinates: coords.map((c: number[]) => ({ lat: c[1], lng: c[0] })),
              confidence: f.properties.confidence || 80,
              areaHa: f.properties.areaHa || 0,
              class: f.properties.class || 'field',
            }
          })
          setYoloFields(allFields)
          
          // AGC: Keep the same selected index if valid, otherwise reset to 0
          const newIndex = selectedYoloIndex < allFields.length ? selectedYoloIndex : 0
          setSelectedYoloIndex(newIndex)
          
          // AGC: Update the displayed lot
          const selectedField = allFields[newIndex]
          if (selectedField) {
            setDetectedLot({
              detectionId: selectedField.id,
              coordinates: selectedField.coordinates,
              confidence: selectedField.confidence / 100,
              metrics: {
                areaHectares: selectedField.areaHa,
                areaSquareMeters: selectedField.areaHa * 10000,
                perimeterMeters: calcPerimeterMeters(selectedField.coordinates),
                perimeterKilometers: calcPerimeterMeters(selectedField.coordinates) / 1000,
                // AGC: centroid as {lat,lng} object (not tuple), averaged across vertices
                centroid: selectedField.coordinates.length > 0
                  ? {
                      lat: selectedField.coordinates.reduce((s, c) => s + c[0], 0) / selectedField.coordinates.length,
                      lng: selectedField.coordinates.reduce((s, c) => s + c[1], 0) / selectedField.coordinates.length,
                    }
                  : storedClickPoint,
                boundingBox: { northEast: { lat: 0, lng: 0 }, southWest: { lat: 0, lng: 0 } },
              },
              metadata: {
                modelUsed: 'yolo-grid',
                processingTimeMs: result.data.metadata?.processingTimeMs || 0,
                detectedLotType: (selectedField.class as any) || 'unknown',
                aiNotes: `Campo ${newIndex + 1} de ${allFields.length}`,
              },
            })
          }
          
          setDetectionInfo({
            method: 'yolo-grid',
            confidence: selectedField?.confidence || 80,
            usedFallback: false,
            processingTimeMs: result.data.metadata?.processingTimeMs || 0,
            fieldType: selectedField?.class,
          })
        } else {
          // AGC: Standard color detection response
          const lot: DetectedLot = {
            detectionId: result.data.detectionId || `det-${Date.now()}`,
            coordinates: result.data.coordinates,
            confidence: result.data.confidence,
            metrics: result.data.metrics || (() => {
              const c = result.data.coordinates || []
              const p = calcPerimeterMeters(c)
              return {
                areaHectares: 0,
                areaSquareMeters: 0,
                perimeterMeters: p,
                perimeterKilometers: p / 1000,
                centroid: storedClickPoint,
                boundingBox: { northEast: { lat: 0, lng: 0 }, southWest: { lat: 0, lng: 0 } },
              }
            })(),
            metadata: {
              modelUsed: result.data.metadata?.modelUsed || result.data.method || detectionMethod,
              processingTimeMs: result.data.metadata?.processingTimeMs || 0,
              detectedLotType: result.data.metadata?.detectedFieldType || 'unknown',
              aiNotes: result.data.suggestedCrop || '',
            } as any,
          }
          
          setDetectedLot(lot)
          setDetectionInfo({
            method: result.data.method || detectionMethod,
            confidence: result.data.confidence,
            usedFallback: result.data.usedFallback || false,
            processingTimeMs: result.data.metadata?.processingTimeMs || 0,
            fieldType: result.data.metadata?.detectedFieldType,
          })
        }
        
        console.log('✅ AGC: Real-time update complete!')
      }
    } catch (error) {
      console.error('⚠️ AGC: Real-time update failed:', error)
    }
    
    setIsRealTimeUpdate(false)
  }, [lastDetectionRequest, phase, detectionMethod, colorOptions, yoloOptions, selectedYoloIndex])
  
  // AGC: NEW - Debounced real-time update when colorOptions change
  // AGC: Using a ref to track the latest colorOptions to avoid infinite loops
  const colorOptionsRef = useRef(colorOptions)
  const isFirstRender = useRef(true)
  
  useEffect(() => {
    // AGC: Skip first render (initial mount)
    if (isFirstRender.current) {
      isFirstRender.current = false
      colorOptionsRef.current = colorOptions
      return
    }
    
    // AGC: Only trigger if we have stored detection data and are in preview mode
    if (!lastDetectionRequest || phase !== 'preview') {
      colorOptionsRef.current = colorOptions
      return
    }
    
    // AGC: Check if colorOptions actually changed
    const hasChanged = JSON.stringify(colorOptionsRef.current) !== JSON.stringify(colorOptions)
    colorOptionsRef.current = colorOptions
    
    if (!hasChanged) return
    
    // AGC: Clear any pending debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    
    // AGC: Debounce the update (500ms delay for better UX)
    debounceTimerRef.current = setTimeout(() => {
      console.log('🔄 AGC: Triggering real-time update due to settings change...')
      runRealTimeUpdate()
    }, 500)
    
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [colorOptions, lastDetectionRequest, phase, runRealTimeUpdate])

  // AGC: NEW - Debounced real-time update when yoloOptions change (YOLO detection)
  // AGC: Similar to colorOptions but for YOLO grid scan
  const yoloOptionsRef = useRef(yoloOptions)
  const isFirstYoloRender = useRef(true)
  
  useEffect(() => {
    // AGC: Skip first render (initial mount)
    if (isFirstYoloRender.current) {
      isFirstYoloRender.current = false
      yoloOptionsRef.current = yoloOptions
      return
    }
    
    // AGC: Only trigger if we're in YOLO mode, have stored detection data, and in preview mode
    if (detectionMethod !== 'yolo-grid' || !lastDetectionRequest || phase !== 'preview') {
      yoloOptionsRef.current = yoloOptions
      return
    }
    
    // AGC: Check if yoloOptions actually changed
    const hasChanged = JSON.stringify(yoloOptionsRef.current) !== JSON.stringify(yoloOptions)
    yoloOptionsRef.current = yoloOptions
    
    if (!hasChanged) return
    
    // AGC: Clear any pending debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    
    // AGC: Debounce the update (500ms delay for better UX)
    debounceTimerRef.current = setTimeout(() => {
      console.log('🔄 AGC: Triggering YOLO real-time update due to settings change...')
      runRealTimeUpdate()
    }, 500)
    
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [yoloOptions, lastDetectionRequest, phase, detectionMethod, runRealTimeUpdate])

  // AGC: Handle refinement point tap (SAM 2 interactive correction)
  const handleRefinementTap = useCallback(async (lat: number, lng: number) => {
    if (!clickPoint || !pipelineCache || refinementRound >= 3) return

    const label = refinementMode === 'add-positive' ? 1 : 0
    const newPoint: GeoPoint = { lat, lng }
    const newPositive = label === 1 ? [...positivePoints, newPoint] : positivePoints
    const newNegative = label === 0 ? [...negativePoints, newPoint] : negativePoints

    setPositivePoints(newPositive)
    setNegativePoints(newNegative)
    setRefinementMode('none')
    setPhase('detecting')

    const allAdditionalPoints = [
      ...newPositive.map(p => ({ ...p, label: 1 })),
      ...newNegative.map(p => ({ ...p, label: 0 })),
    ]

    const newRound = refinementRound + 1

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/lots/detect/refine`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clickPoint,
          additionalPoints: allAdditionalPoints,
          refinementRound: newRound,
          cache: pipelineCache,
        }),
      })

      const result = await response.json()

      if (result.success && result.data) {
        setRefinementRound(newRound)
        setCanRefine(newRound < 3)
        if (result.cache) setPipelineCache(result.cache)

        // AGC: Update detected lot with refined polygon
        setDetectedLot({
          detectionId: result.data.detectionId,
          coordinates: result.data.coordinates,
          confidence: result.data.confidence,
          metrics: detectedLot?.metrics || { areaHectares: 0, areaSquareMeters: 0, perimeterMeters: 0, perimeterKilometers: 0, centroid: { lat: 0, lng: 0 }, boundingBox: { northEast: { lat: 0, lng: 0 }, southWest: { lat: 0, lng: 0 } } },
          metadata: {
            modelUsed: result.data.metadata?.modelUsed || 'sam2-hiera-large',
            processingTimeMs: result.data.metadata?.processingTimeMs || 0,
            detectedLotType: 'unknown',
          },
        })
        setDetectionInfo({
          method: 'sam',
          confidence: result.data.confidence,
          usedFallback: false,
          processingTimeMs: result.data.metadata?.processingTimeMs || 0,
        })
        setPhase('preview')
      } else {
        setPhase('preview') // AGC: Go back to preview, don't lose polygon
        setErrorMessage(result.error?.message || 'Refinement failed')
      }
    } catch (error) {
      setPhase('preview')
      setErrorMessage('Erro no refinamento')
    }
  }, [clickPoint, pipelineCache, refinementRound, refinementMode, positivePoints, negativePoints, detectedLot])

  // AGC: Handle map click - start detection (single point mode)
  const handleMapClick = useCallback(async (lat: number, lng: number) => {
    // AGC: If in refinement mode, handle as refinement tap
    if (refinementMode === 'add-positive' || refinementMode === 'add-negative') {
      handleRefinementTap(lat, lng)
      return
    }

    if (phase !== 'idle') return

    // AGC: Reset refinement state for new detection
    setRefinementRound(0)
    setPositivePoints([])
    setNegativePoints([])
    setCanRefine(false)
    setPipelineCache(null)
    setRefinementMode('none')

    setClickPoint({ lat, lng })
    setSamplePolygon(null)
    setPhase('detecting')
    setErrorMessage('')
    await runDetection(lat, lng)
  }, [phase, detectionMethod, colorOptions, refinementMode, handleRefinementTap])

  // AGC: Confirm detection and go to FinalizarLote for name/crop
  const handleConfirm = async () => {
    if (!detectedLot) return
    
    try {
      // AGC: Pre-fill lot name when YOLO: use editable name for current field
      const suggestedName =
        yoloFields.length > 0 && yoloFieldNames[selectedYoloIndex]
          ? yoloFieldNames[selectedYoloIndex]
          : undefined
      // AGC: Navigate to FinalizarLote where user enters name and selects crop
      // AGC: Forward lotName: YOLO suggestion > original name from NovoLote
      const forwardedName = suggestedName || initialLotName
      onNavigate('finalizar-lote', {
        coordinates: detectedLot.coordinates,
        metrics: detectedLot.metrics,
        detectionMethod: detectionInfo?.method || detectionMethod,
        aiProvider: detectedLot.metadata.modelUsed,
        usedFallback: detectionInfo?.usedFallback || false,
        ...(forwardedName ? { lotName: forwardedName } : {}),
      })
    } catch (error) {
      console.error('AGC: Navigation error:', error)
      setErrorMessage('Erro ao continuar')
      setPhase('error')
    }
  }

  // AGC: Reset to try again
  const handleRetry = () => {
    setPhase('idle')
    setDetectedLot(null)
    setClickPoint(null)
    setErrorMessage('')
    setDetectionInfo(null)
    // AGC: Reset draw state
    setDrawPoints([])
    // AGC: Reset YOLO fields and names
    setYoloFields([])
    setSelectedYoloIndex(0)
    setYoloFieldNames([])
    setSamplePolygon(null)
    // AGC: Clear stored detection request (stops real-time updates)
    setLastDetectionRequest(null)
    setIsRealTimeUpdate(false)
    // AGC: Reset SAM 2 refinement state
    setRefinementMode('none')
    setRefinementRound(0)
    setPositivePoints([])
    setNegativePoints([])
    setCanRefine(false)
    setPipelineCache(null)
  }

  // AGC: Format area for display
  const formatArea = (hectares: number): string => {
    if (hectares >= 1) {
      return `${hectares.toFixed(2)} ha`
    }
    return `${(hectares * 10000).toFixed(0)} m²`
  }

  // AGC: Format perimeter for display
  const formatPerimeter = (meters: number): string => {
    if (meters >= 1000) {
      return `${(meters / 1000).toFixed(2)} km`
    }
    return `${meters.toFixed(0)} m`
  }

  // AGC: Get current method config
  const currentMethodConfig = DETECTION_METHODS[detectionMethod]
  const MethodIcon = currentMethodConfig.icon

  return (
    // AGC: h-full (not min-h-screen) so the page fits exactly inside MainMenu's <main>
    // AGC: container instead of overflowing it. With min-h-screen the page was 100vh tall,
    // AGC: but main is shorter than 100vh (AppHeader takes the top), so the bottom of the map
    // AGC: — and the absolute bottom-safe-4 instruction card — ended up below the visible
    // AGC: scroll area. h-full constrains the page to main's actual height.
    <div className="h-full bg-[#F4F5F7] flex flex-col rounded-none">
      {/* AGC: Header */}
      <header className="bg-gradient-to-r from-emerald-600 via-green-600 to-teal-600 px-4 pt-4 pb-4 sticky top-0 z-20">
        <div className="flex items-center justify-between mb-2">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-white"
          >
            <ArrowLeft className="w-5 h-5" />
            Voltar
          </button>
          
          {/* AGC: Detection Method Selector — Color/YOLO as fallback options */}
          <div className="relative">
            <button
              onClick={() => setShowMethodSelector(!showMethodSelector)}
              className="flex items-center gap-2 bg-white/20 px-3 py-1.5 rounded-full hover:bg-white/30 transition-colors"
            >
              <MethodIcon className="w-4 h-4 text-white" />
              <span className="text-sm font-medium text-white">{currentMethodConfig.name}</span>
              <ChevronDown className={`w-4 h-4 text-white transition-transform ${showMethodSelector ? 'rotate-180' : ''}`} />
            </button>

            {showMethodSelector && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden z-50">
                {Object.values(DETECTION_METHODS)
                  .filter((m) => AI_ONE_CLICK_METHOD_IDS.includes(m.id))
                  .map((method) => {
                  const Icon = method.icon
                  const isSelected = detectionMethod === method.id
                  return (
                    <button
                      key={method.id}
                      onClick={() => {
                        setDetectionMethod(method.id)
                        setShowMethodSelector(false)
                      }}
                      className={`w-full px-4 py-3 flex items-start gap-3 transition-colors ${
                        isSelected ? 'bg-emerald-50 hover:bg-emerald-100' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        isSelected ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600'
                      }`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 text-left">
                        <span className={`font-medium ${isSelected ? 'text-emerald-700' : 'text-gray-900'}`}>
                          {method.name}
                        </span>
                        {method.recommended && (
                          <span className="ml-2 text-[10px] font-bold text-white bg-emerald-500 px-1.5 py-0.5 rounded">REC</span>
                        )}
                        <p className="text-xs text-gray-500">{method.description}</p>
                        <p className="text-xs font-medium mt-0.5 text-emerald-600">{method.cost}</p>
                      </div>
                      {isSelected && <Check className="w-5 h-5 text-emerald-500" />}
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {/* AGC: Dev Panel Toggle - Only show for color detection */}
          {detectionMethod === 'color' && (
            <button
              onClick={() => setShowDevPanel(!showDevPanel)}
              className={`flex items-center gap-1 px-2 py-1.5 rounded-full transition-colors ${
                showDevPanel ? 'bg-yellow-400 text-black' : 'bg-white/20 text-white hover:bg-white/30'
              }`}
              title="Parâmetros de detecção (Dev)"
            >
              <Sliders className="w-4 h-4" />
            </button>
          )}

          {/* AGC: YOLO Settings Toggle - Only show for YOLO detection */}
          {detectionMethod === 'yolo-grid' && (
            <button
              onClick={() => setShowYoloPanel(!showYoloPanel)}
              className={`flex items-center gap-1 px-2 py-1.5 rounded-full transition-colors ${
                showYoloPanel ? 'bg-blue-400 text-black' : 'bg-white/20 text-white hover:bg-white/30'
              }`}
              title="Parâmetros YOLO (Dev)"
            >
              <Sliders className="w-4 h-4" />
            </button>
          )}
          
          {/* AGC: NEW - Sample Mode Toggle (Click vs Draw) */}
          <div className="flex items-center gap-1 bg-white/10 rounded-full p-0.5">
            <button
              onClick={() => setSampleMode('click')}
              className={`px-2 py-1 text-xs rounded-full transition-colors ${
                sampleMode === 'click' 
                  ? 'bg-white text-emerald-700 font-medium' 
                  : 'text-white/80 hover:text-white'
              }`}
              title="Toque único"
            >
              👆 Toque
            </button>
            <button
              onClick={() => setSampleMode('draw')}
              className={`px-2 py-1 text-xs rounded-full transition-colors ${
                sampleMode === 'draw' 
                  ? 'bg-white text-emerald-700 font-medium' 
                  : 'text-white/80 hover:text-white'
              }`}
              title="Desenhar área para amostra de cor"
            >
              ✏️ Área
            </button>
          </div>
        </div>
        
        <h1 className="text-white text-xl font-bold">Detectar lote</h1>

        {/* AGC: Phase-aware instruction line. Previously this only rendered for
            phase === 'idle' / 'drawing', so once the user ran a YOLO scan and
            entered the preview phase the box went blank — leaving them with
            no guidance about what to do next. Now every phase has a hint, so
            the instruction "box text" never disappears. */}
        {phase === 'idle' && (
          <p className="text-white/80 text-sm mt-1">
            {sampleMode === 'click'
              ? 'Toque no mapa para detectar o lote automaticamente'
              : 'Desenhe uma área para amostrar a cor média do campo'
            }
          </p>
        )}
        {phase === 'drawing' && (
          <p className="text-white/80 text-sm mt-1 animate-pulse">
            Arraste para selecionar a área de amostra...
          </p>
        )}
        {phase === 'detecting' && (
          <p className="text-white/80 text-sm mt-1 animate-pulse">
            Analisando imagem do satélite...
          </p>
        )}
        {phase === 'preview' && (
          <p className="text-white/80 text-sm mt-1">
            {yoloFields.length > 1
              ? 'Toque em outro talhão no mapa para trocar, ou confirme abaixo'
              : 'Confira o lote detectado abaixo e confirme'
            }
          </p>
        )}
        {phase === 'error' && (
          <p className="text-white/80 text-sm mt-1">
            Toque em "Tentar novamente" para detectar de novo
          </p>
        )}
      </header>

      {/* AGC: Developer Tuning Panel - Collapsible */}
      {showDevPanel && detectionMethod === 'color' && (
        <div className="bg-gray-900 text-white p-3 border-b border-gray-700 z-30 max-h-[40vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-yellow-400 flex items-center gap-1">
              <Sliders className="w-3 h-3" />
              DEV: Parâmetros de Cor
              {/* AGC: Real-time update indicator */}
              {isRealTimeUpdate && (
                <span className="ml-2 text-green-400 animate-pulse">⟳ Atualizando...</span>
              )}
              {phase === 'preview' && lastDetectionRequest && !isRealTimeUpdate && (
                <span className="ml-2 text-green-400 text-[10px]">✓ Alterações atualizam em tempo real</span>
              )}
            </span>
            <button
              onClick={() => setColorOptions(DEFAULT_COLOR_OPTIONS)}
              className="text-xs bg-gray-700 px-2 py-0.5 rounded hover:bg-gray-600"
            >
              Reset
            </button>
          </div>
          
          {/* AGC: Auto-detected preset indicator */}
          {autoPreset && (
            <div className="bg-blue-900/40 border border-blue-500 rounded p-2 mb-3">
              <div className="flex items-center gap-2">
                <span className="text-lg">{autoPreset.presetName.split(' ')[0]}</span>
                <div>
                  <p className="text-xs font-bold text-blue-300">
                    🎯 Preset auto-detectado: {autoPreset.presetName}
                  </p>
                  <p className="text-[10px] text-blue-400">
                    {autoPreset.reason}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* AGC: Preset Buttons - organized by field type */}
          <div className="mb-3">
            <p className="text-xs text-gray-500 mb-1">🎨 Presets por tipo de campo:</p>
            
            {/* AGC: Field Color Presets (1-5) */}
            <div className="grid grid-cols-5 gap-1 mb-2">
              {COLOR_PRESETS.slice(0, 5).map(preset => (
                <button
                  key={preset.id}
                  onClick={() => {
                    const { id, name, desc, ...presetOptions } = preset
                    setColorOptions({ ...DEFAULT_COLOR_OPTIONS, ...presetOptions })
                  }}
                  className={`text-[10px] px-1 py-2 rounded transition-colors ${
                    colorOptions.hueTolerance === preset.hueTolerance && 
                    colorOptions.satTolerance === preset.satTolerance
                      ? 'bg-yellow-500 text-black font-bold'
                      : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  title={preset.desc}
                >
                  {preset.name}
                </button>
              ))}
            </div>
            
            {/* AGC: Special Presets (6-10) */}
            <div className="grid grid-cols-5 gap-1">
              {COLOR_PRESETS.slice(5, 10).map(preset => (
                <button
                  key={preset.id}
                  onClick={() => {
                    const { id, name, desc, ...presetOptions } = preset
                    setColorOptions({ ...DEFAULT_COLOR_OPTIONS, ...presetOptions })
                  }}
                  className={`text-[10px] px-1 py-2 rounded transition-colors ${
                    preset.useLineDetection 
                      ? (colorOptions.useLineDetection ? 'bg-orange-500 text-black font-bold' : 'bg-orange-900/50 hover:bg-orange-700')
                      : preset.straightenEdges
                        ? (colorOptions.straightenEdges ? 'bg-blue-500 text-black font-bold' : 'bg-blue-900/50 hover:bg-blue-700')
                        : preset.useConvexHull
                          ? (colorOptions.useConvexHull ? 'bg-purple-500 text-black font-bold' : 'bg-purple-900/50 hover:bg-purple-700')
                          : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  title={preset.desc}
                >
                  {preset.name}
                </button>
              ))}
            </div>
          </div>
          
          {/* AGC: AUTO-ADAPT MODE - Most important toggle */}
          <div className="bg-green-900/30 border border-green-700 rounded p-2 mb-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={colorOptions.useAutoAdapt || false}
                onChange={(e) => setColorOptions(prev => ({ ...prev, useAutoAdapt: e.target.checked }))}
                className="w-4 h-4 rounded accent-green-500"
              />
              <span className="text-xs font-bold text-green-400">
                🎯 AUTO-ADAPT: Ajusta tolerâncias automaticamente
              </span>
            </label>
            <p className="text-[10px] text-green-600 mt-1 ml-6">
              Analisa a variância de cores na área desenhada e ajusta H/S/V automaticamente
            </p>
          </div>
          
          {/* AGC: Current Values Display */}
          <div className="bg-gray-800 rounded p-2 mb-2 text-xs font-mono">
            H:{colorOptions.hueTolerance} S:{colorOptions.satTolerance} V:{colorOptions.valTolerance} E:{colorOptions.edgeGradientThreshold} F:{Math.round(colorOptions.maxFillRatio * 100)}%
            {colorOptions.useAutoAdapt && <span className="text-green-400 ml-2">(Auto)</span>}
          </div>
          
          <div className="grid grid-cols-2 gap-2 text-xs">
            {/* AGC: Hue Tolerance */}
            <div>
              <label className="text-gray-400">Hue Tol: {colorOptions.hueTolerance}</label>
              <input
                type="range"
                min="5"
                max="50"
                value={colorOptions.hueTolerance}
                onChange={(e) => setColorOptions(prev => ({ ...prev, hueTolerance: parseInt(e.target.value) }))}
                className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
              />
            </div>
            
            {/* AGC: Saturation Tolerance */}
            <div>
              <label className="text-gray-400">Sat Tol: {colorOptions.satTolerance}</label>
              <input
                type="range"
                min="10"
                max="100"
                value={colorOptions.satTolerance}
                onChange={(e) => setColorOptions(prev => ({ ...prev, satTolerance: parseInt(e.target.value) }))}
                className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
              />
            </div>
            
            {/* AGC: Value Tolerance */}
            <div>
              <label className="text-gray-400">Val Tol: {colorOptions.valTolerance}</label>
              <input
                type="range"
                min="10"
                max="100"
                value={colorOptions.valTolerance}
                onChange={(e) => setColorOptions(prev => ({ ...prev, valTolerance: parseInt(e.target.value) }))}
                className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
              />
            </div>
            
            {/* AGC: Edge Gradient Threshold */}
            <div>
              <label className="text-gray-400">Edge: {colorOptions.edgeGradientThreshold}</label>
              <input
                type="range"
                min="5"
                max="50"
                value={colorOptions.edgeGradientThreshold}
                onChange={(e) => setColorOptions(prev => ({ ...prev, edgeGradientThreshold: parseInt(e.target.value) }))}
                className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
              />
            </div>
            
            {/* AGC: Max Fill Ratio */}
            <div className="col-span-2">
              <label className="text-gray-400">Max Fill: {(colorOptions.maxFillRatio * 100).toFixed(0)}%</label>
              <input
                type="range"
                min="10"
                max="80"
                value={colorOptions.maxFillRatio * 100}
                onChange={(e) => setColorOptions(prev => ({ ...prev, maxFillRatio: parseInt(e.target.value) / 100 }))}
                className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
              />
            </div>
          </div>
          
          {/* AGC: NEW - Filter Section */}
          <div className="mt-3 pt-3 border-t border-gray-700">
            <h4 className="text-xs font-semibold text-emerald-400 mb-2">🔧 FILTROS</h4>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {/* AGC: Gaussian Blur */}
              <div>
                <label className="text-gray-400">Blur: {colorOptions.gaussianBlur}</label>
                <input
                  type="range"
                  min="0"
                  max="5"
                  value={colorOptions.gaussianBlur}
                  onChange={(e) => setColorOptions(prev => ({ ...prev, gaussianBlur: parseInt(e.target.value) }))}
                  className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                />
              </div>
              
              {/* AGC: Median Filter */}
              <div>
                <label className="text-gray-400">Median: {colorOptions.medianFilter}</label>
                <input
                  type="range"
                  min="0"
                  max="7"
                  step="2"
                  value={colorOptions.medianFilter}
                  onChange={(e) => setColorOptions(prev => ({ ...prev, medianFilter: parseInt(e.target.value) }))}
                  className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                />
              </div>
              
              {/* AGC: Polygon Smoothing - Fine control with 0.25 steps */}
              <div>
                <label className="text-gray-400">Smooth: {(colorOptions.polygonSmoothing || 0).toFixed(2)}</label>
                <input
                  type="range"
                  min="0"
                  max="1.75"
                  step="0.25"
                  value={colorOptions.polygonSmoothing || 0}
                  onChange={(e) => setColorOptions(prev => ({ ...prev, polygonSmoothing: parseFloat(e.target.value) }))}
                  className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                />
              </div>
              
              {/* AGC: Polygon Expansion - Expand outward to cover corners */}
              <div>
                <label className="text-gray-400">Expand: {colorOptions.polygonExpansion || 0}px</label>
                <input
                  type="range"
                  min="0"
                  max="15"
                  step="1"
                  value={colorOptions.polygonExpansion || 0}
                  onChange={(e) => setColorOptions(prev => ({ ...prev, polygonExpansion: parseInt(e.target.value) }))}
                  className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                />
              </div>
              
              {/* AGC: Chaikin Smoothing */}
              <div>
                <label className="text-gray-400">Chaikin: {colorOptions.chaikinSmoothing}</label>
                <input
                  type="range"
                  min="0"
                  max="3"
                  value={colorOptions.chaikinSmoothing}
                  onChange={(e) => setColorOptions(prev => ({ ...prev, chaikinSmoothing: parseInt(e.target.value) }))}
                  className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                />
              </div>
              
              {/* AGC: Convex Hull Toggle */}
              <div className="col-span-2 flex items-center gap-2 mt-1">
                <input
                  type="checkbox"
                  id="convexHull"
                  checked={colorOptions.useConvexHull}
                  onChange={(e) => setColorOptions(prev => ({ ...prev, useConvexHull: e.target.checked }))}
                  className="w-4 h-4 accent-emerald-500"
                />
                <label htmlFor="convexHull" className="text-gray-400">Convex Hull (forma limpa)</label>
              </div>
            </div>
          </div>
          
          {/* AGC: NEW - Line/Road Detection Section */}
          <div className="mt-3 pt-3 border-t border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold text-orange-400">🛣️ DETECÇÃO DE ESTRADAS</h4>
              <label className="flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={colorOptions.useLineDetection || false}
                  onChange={(e) => setColorOptions(prev => ({ ...prev, useLineDetection: e.target.checked }))}
                  className="w-3 h-3 accent-orange-500"
                />
                <span className="text-xs text-gray-400">Ativar (+2-5s)</span>
              </label>
            </div>
            <p className="text-[10px] text-orange-600 mb-2">
              🔍 Detecta linhas/estradas como barreiras (mais lento, mas melhor resultado)
            </p>
            
            {colorOptions.useLineDetection && (
              <div className="grid grid-cols-2 gap-2 text-xs">
                {/* AGC: Hough Threshold */}
                <div>
                  <label className="text-gray-400">Sensibilidade: {colorOptions.lineHoughThreshold || 40}</label>
                  <input
                    type="range"
                    min="20"
                    max="80"
                    value={colorOptions.lineHoughThreshold || 40}
                    onChange={(e) => setColorOptions(prev => ({ ...prev, lineHoughThreshold: parseInt(e.target.value) }))}
                    className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  />
                </div>
                
                {/* AGC: Road Max Saturation */}
                <div>
                  <label className="text-gray-400">Sat. Estrada: {colorOptions.roadMaxSaturation || 80}</label>
                  <input
                    type="range"
                    min="30"
                    max="120"
                    value={colorOptions.roadMaxSaturation || 80}
                    onChange={(e) => setColorOptions(prev => ({ ...prev, roadMaxSaturation: parseInt(e.target.value) }))}
                    className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  />
                </div>
                
                {/* AGC: Line Barrier Dilate */}
                <div className="col-span-2">
                  <label className="text-gray-400">Espessura barreira: {colorOptions.lineBarrierDilate || 3}px</label>
                  <input
                    type="range"
                    min="1"
                    max="8"
                    value={colorOptions.lineBarrierDilate || 3}
                    onChange={(e) => setColorOptions(prev => ({ ...prev, lineBarrierDilate: parseInt(e.target.value) }))}
                    className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  />
                </div>
              </div>
            )}
          </div>
          
          {/* AGC: NEW - Advanced Mode Section (Lab + Mahalanobis) - DISABLED WARNING */}
          <div className="mt-3 pt-3 border-t border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold text-purple-400">🧠 MODO AVANÇADO (Lab)</h4>
              <label className="flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={colorOptions.useAdvancedMode || false}
                  onChange={(e) => setColorOptions(prev => ({ ...prev, useAdvancedMode: e.target.checked }))}
                  className="w-3 h-3 accent-purple-500"
                />
                <span className="text-xs text-red-400">⚠️ Experimental</span>
              </label>
            </div>
            
            {colorOptions.useAdvancedMode && (
              <div className="grid grid-cols-2 gap-2 text-xs">
                {/* AGC: Chroma Threshold */}
                <div>
                  <label className="text-gray-400">Chroma: {colorOptions.chromaThreshold?.toFixed(1)}</label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    step="0.5"
                    value={colorOptions.chromaThreshold || 3}
                    onChange={(e) => setColorOptions(prev => ({ ...prev, chromaThreshold: parseFloat(e.target.value) }))}
                    className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  />
                </div>
                
                {/* AGC: Luminance Threshold */}
                <div>
                  <label className="text-gray-400">Lum: {colorOptions.lumThreshold?.toFixed(1)}</label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    step="0.5"
                    value={colorOptions.lumThreshold || 2.5}
                    onChange={(e) => setColorOptions(prev => ({ ...prev, lumThreshold: parseFloat(e.target.value) }))}
                    className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  />
                </div>
                
                {/* AGC: Barrier Threshold */}
                <div>
                  <label className="text-gray-400">Barreira: {colorOptions.barrierThreshold}</label>
                  <input
                    type="range"
                    min="10"
                    max="50"
                    value={colorOptions.barrierThreshold || 25}
                    onChange={(e) => setColorOptions(prev => ({ ...prev, barrierThreshold: parseInt(e.target.value) }))}
                    className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  />
                </div>
                
                {/* AGC: Barrier Dilate */}
                <div>
                  <label className="text-gray-400">Dilatar: {colorOptions.barrierDilateRadius}px</label>
                  <input
                    type="range"
                    min="0"
                    max="3"
                    value={colorOptions.barrierDilateRadius || 1}
                    onChange={(e) => setColorOptions(prev => ({ ...prev, barrierDilateRadius: parseInt(e.target.value) }))}
                    className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  />
                </div>
                
                {/* AGC: Edge Barrier Toggle */}
                <div className="col-span-2 flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="edgeBarrier"
                    checked={colorOptions.useEdgeBarrier !== false}
                    onChange={(e) => setColorOptions(prev => ({ ...prev, useEdgeBarrier: e.target.checked }))}
                    className="w-3 h-3 accent-purple-500"
                  />
                  <label htmlFor="edgeBarrier" className="text-gray-400">Barreira de borda (bloqueia estradas)</label>
                </div>
              </div>
            )}
            
            <p className="text-xs text-purple-300/60 mt-1">
              Lab + Mahalanobis = melhor detecção de sombras e campos similares
            </p>
          </div>
          
          {/* AGC: NEW - Edge Detection Fallback Section (Canny/LoG) */}
          <div className="mt-3 pt-3 border-t border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold text-blue-400">🔲 DETECÇÃO DE BORDAS (Fallback)</h4>
              <label className="flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={colorOptions.useEdgeFallback !== false}
                  onChange={(e) => setColorOptions(prev => ({ ...prev, useEdgeFallback: e.target.checked }))}
                  className="w-3 h-3 accent-blue-500"
                />
                <span className="text-xs text-gray-400">Ativar</span>
              </label>
            </div>
            
            <p className="text-xs text-blue-300/60 mb-2">
              Quando confiança &lt;{((colorOptions.edgeFallbackThreshold || 0.7) * 100).toFixed(0)}%, usa Canny/LoG
            </p>
            
            {colorOptions.useEdgeFallback !== false && (
              <div className="grid grid-cols-2 gap-2 text-xs">
                {/* AGC: Force Edge Detection (for testing) */}
                <div className="col-span-2 flex items-center gap-2 mb-1">
                  <input
                    type="checkbox"
                    id="forceEdgeDetection"
                    checked={colorOptions.forceEdgeDetection || false}
                    onChange={(e) => setColorOptions(prev => ({ ...prev, forceEdgeDetection: e.target.checked }))}
                    className="w-3 h-3 accent-blue-500"
                  />
                  <label htmlFor="forceEdgeDetection" className="text-gray-400">
                    🔧 Forçar detecção de bordas (teste - ignora threshold)
                  </label>
                </div>
                
                {/* AGC: Fallback Threshold */}
                <div className="col-span-2">
                  <label className="text-gray-400">Threshold: {((colorOptions.edgeFallbackThreshold || 0.7) * 100).toFixed(0)}%</label>
                  <input
                    type="range"
                    min="50"
                    max="90"
                    value={(colorOptions.edgeFallbackThreshold || 0.7) * 100}
                    onChange={(e) => setColorOptions(prev => ({ ...prev, edgeFallbackThreshold: parseInt(e.target.value) / 100 }))}
                    className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                    disabled={colorOptions.forceEdgeDetection}
                  />
                </div>
                
                {/* AGC: Use LoG Toggle */}
                <div className="col-span-2 flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="useLoG"
                    checked={colorOptions.useLoG || false}
                    onChange={(e) => setColorOptions(prev => ({ ...prev, useLoG: e.target.checked }))}
                    className="w-3 h-3 accent-blue-500"
                  />
                  <label htmlFor="useLoG" className="text-gray-400">Usar LoG (multi-escala) ao invés de Canny</label>
                </div>
                
                {/* AGC: Canny Low Threshold */}
                <div>
                  <label className="text-gray-400">Canny Low: {colorOptions.cannyLowThreshold || 50}</label>
                  <input
                    type="range"
                    min="20"
                    max="100"
                    value={colorOptions.cannyLowThreshold || 50}
                    onChange={(e) => setColorOptions(prev => ({ ...prev, cannyLowThreshold: parseInt(e.target.value) }))}
                    className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  />
                </div>
                
                {/* AGC: Canny High Threshold */}
                <div>
                  <label className="text-gray-400">Canny High: {colorOptions.cannyHighThreshold || 150}</label>
                  <input
                    type="range"
                    min="80"
                    max="250"
                    value={colorOptions.cannyHighThreshold || 150}
                    onChange={(e) => setColorOptions(prev => ({ ...prev, cannyHighThreshold: parseInt(e.target.value) }))}
                    className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  />
                </div>
                
                {/* AGC: LoG Sigma */}
                <div>
                  <label className="text-gray-400">LoG Sigma: {(colorOptions.logSigma || 1.5).toFixed(1)}</label>
                  <input
                    type="range"
                    min="0.5"
                    max="3"
                    step="0.5"
                    value={colorOptions.logSigma || 1.5}
                    onChange={(e) => setColorOptions(prev => ({ ...prev, logSigma: parseFloat(e.target.value) }))}
                    className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  />
                </div>
                
                {/* AGC: Edge Dilate Radius */}
                <div>
                  <label className="text-gray-400">Dilatar: {colorOptions.edgeDilateRadius || 2}px</label>
                  <input
                    type="range"
                    min="0"
                    max="5"
                    value={colorOptions.edgeDilateRadius || 2}
                    onChange={(e) => setColorOptions(prev => ({ ...prev, edgeDilateRadius: parseInt(e.target.value) }))}
                    className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  />
                </div>
                
                {/* AGC: Watershed Refinement Toggle */}
                <div className="col-span-2 flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="useWatershed"
                    checked={colorOptions.useWatershed || false}
                    onChange={(e) => setColorOptions(prev => ({ ...prev, useWatershed: e.target.checked }))}
                    className="w-3 h-3 accent-blue-500"
                  />
                  <label htmlFor="useWatershed" className="text-gray-400">Watershed (refinamento, lento)</label>
                </div>
              </div>
            )}
          </div>
          
          {/* AGC: NEW - Polygon Straightening Section */}
          <div className="mt-3 pt-3 border-t border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold text-green-400">📐 BORDAS RETAS (Fazenda)</h4>
              <label className="flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={colorOptions.straightenEdges !== false}
                  onChange={(e) => setColorOptions(prev => ({ ...prev, straightenEdges: e.target.checked }))}
                  className="w-3 h-3 accent-green-500"
                />
                <span className="text-xs text-gray-400">Ativar</span>
              </label>
            </div>
            
            <p className="text-xs text-green-300/60 mb-2">
              Detecta segmentos quase retos e os torna perfeitamente retos
            </p>
            
            {colorOptions.straightenEdges !== false && (
              <div className="grid grid-cols-2 gap-2 text-xs">
                {/* AGC: Straightness Threshold */}
                <div>
                  <label className="text-gray-400">Tolerância: {(colorOptions.straightnessThreshold || 3).toFixed(1)}px</label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    step="0.5"
                    value={colorOptions.straightnessThreshold || 3}
                    onChange={(e) => setColorOptions(prev => ({ ...prev, straightnessThreshold: parseFloat(e.target.value) }))}
                    className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  />
                </div>
                
                {/* AGC: Min Segment Length */}
                <div>
                  <label className="text-gray-400">Seg. Min: {colorOptions.minSegmentLength || 20}px</label>
                  <input
                    type="range"
                    min="10"
                    max="50"
                    value={colorOptions.minSegmentLength || 20}
                    onChange={(e) => setColorOptions(prev => ({ ...prev, minSegmentLength: parseInt(e.target.value) }))}
                    className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  />
                </div>
                
                {/* AGC: Snap to Cardinal Angles */}
                <div className="col-span-2 flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="snapToCardinal"
                    checked={colorOptions.snapToCardinal || false}
                    onChange={(e) => setColorOptions(prev => ({ ...prev, snapToCardinal: e.target.checked }))}
                    className="w-3 h-3 accent-green-500"
                  />
                  <label htmlFor="snapToCardinal" className="text-gray-400">Alinhar a 0°/45°/90° (bordas retas)</label>
                </div>
              </div>
            )}
          </div>
          
          {/* AGC: User-saved Presets */}
          {userPresets.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-700">
              <h4 className="text-xs font-semibold text-yellow-400 mb-2">💾 MEUS PRESETS</h4>
              <div className="flex flex-wrap gap-1">
                {userPresets.map(preset => (
                  <div key={preset.id} className="flex items-center gap-1">
                    <button
                      onClick={() => {
                        const { id, name, desc, isUserPreset, createdAt, ...presetOptions } = preset
                        setColorOptions(presetOptions as typeof DEFAULT_COLOR_OPTIONS)
                      }}
                      className="text-[10px] px-2 py-1.5 rounded bg-yellow-700 hover:bg-yellow-600 text-white"
                      title={preset.desc}
                    >
                      {preset.name}
                    </button>
                    <button
                      onClick={() => deleteUserPreset(preset.id)}
                      className="text-[10px] px-1 py-1.5 rounded bg-red-800 hover:bg-red-600 text-white"
                      title="Excluir preset"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* AGC: Save Preset Button */}
          <div className="mt-3 pt-3 border-t border-gray-700">
            <button
              onClick={() => setShowSavePresetModal(true)}
              className="w-full py-2 bg-green-600 hover:bg-green-500 text-white text-sm font-medium rounded-lg flex items-center justify-center gap-2"
            >
              💾 Salvar Configurações Atuais
            </button>
          </div>
          
          {/* AGC: Save Preset Modal */}
          {showSavePresetModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[90]">
              <div className="bg-gray-800 rounded-lg p-4 w-[280px] mx-4">
                <h3 className="text-white font-bold mb-3">💾 Salvar Preset</h3>
                <input
                  type="text"
                  value={newPresetName}
                  onChange={(e) => setNewPresetName(e.target.value)}
                  placeholder="Nome do preset..."
                  onKeyDown={(e) => {
                    // AGC: Android keyboard "Go" → save preset
                    if (e.key === 'Enter' && newPresetName.trim()) { e.preventDefault(); saveUserPreset() }
                  }}
                  enterKeyHint="go"
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg mb-3 text-sm"
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowSavePresetModal(false)}
                    className="flex-1 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg text-sm"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={saveUserPreset}
                    disabled={!newPresetName.trim()}
                    className="flex-1 py-2 bg-green-600 hover:bg-green-500 disabled:bg-gray-500 text-white rounded-lg text-sm"
                  >
                    Salvar
                  </button>
                </div>
              </div>
            </div>
          )}
          
          <p className="text-xs text-gray-500 mt-2">
            Ajuste e clique no mapa para testar. Valores menores = mais restritivo.
          </p>
        </div>
      )}
      
      {/* AGC: YOLO Settings Panel - Collapsible */}
      {showYoloPanel && detectionMethod === 'yolo-grid' && (
        <div className="bg-gray-900 text-white p-3 border-b border-gray-700 z-30 max-h-[40vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold text-blue-400 flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              DEV: Parâmetros YOLO
              {/* AGC: Real-time update indicator */}
              {isRealTimeUpdate && (
                <span className="ml-2 text-green-400 animate-pulse">⟳ Atualizando...</span>
              )}
              {phase === 'preview' && lastDetectionRequest && !isRealTimeUpdate && (
                <span className="ml-2 text-green-400/60 text-[10px]">(tempo real)</span>
              )}
            </span>
            <button
              onClick={() => setYoloOptions(DEFAULT_YOLO_OPTIONS)}
              className="text-xs bg-gray-700 px-2 py-0.5 rounded hover:bg-gray-600"
            >
              Reset
            </button>
          </div>
          
          {/* AGC: Current Values Display */}
          <div className="bg-gray-800 rounded p-2 mb-3 text-xs font-mono">
            Confiança:{(yoloOptions.confidenceThreshold * 100).toFixed(0)}% | 
            Epsilon:{yoloOptions.simplifyEpsilon} | 
            Ângulo:{yoloOptions.straightenAngle}°
          </div>
          
          {/* AGC: Confidence Threshold */}
          <div className="mb-3">
            <label className="text-xs text-gray-400">
              🎯 Confiança mínima: {(yoloOptions.confidenceThreshold * 100).toFixed(0)}%
            </label>
            <input
              type="range"
              min="0.1"
              max="0.8"
              step="0.05"
              value={yoloOptions.confidenceThreshold}
              onChange={(e) => setYoloOptions(prev => ({ ...prev, confidenceThreshold: parseFloat(e.target.value) }))}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-[10px] text-gray-500">
              <span>10% (mais campos)</span>
              <span>80% (menos campos)</span>
            </div>
          </div>
          
          {/* AGC: Simplify Epsilon */}
          <div className="mb-3">
            <label className="text-xs text-gray-400">
              📐 Simplificação (epsilon): {yoloOptions.simplifyEpsilon}
            </label>
            <input
              type="range"
              min="2"
              max="30"
              step="1"
              value={yoloOptions.simplifyEpsilon}
              onChange={(e) => setYoloOptions(prev => ({ ...prev, simplifyEpsilon: parseFloat(e.target.value) }))}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-[10px] text-gray-500">
              <span>2 (detalhado)</span>
              <span>30 (muito limpo)</span>
            </div>
          </div>
          
          {/* AGC: Straighten Angle */}
          <div className="mb-3">
            <label className="text-xs text-gray-400">
              📏 Ângulo de retificação: {yoloOptions.straightenAngle}°
            </label>
            <input
              type="range"
              min="5"
              max="25"
              step="1"
              value={yoloOptions.straightenAngle}
              onChange={(e) => setYoloOptions(prev => ({ ...prev, straightenAngle: parseInt(e.target.value) }))}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-[10px] text-gray-500">
              <span>5° (rígido)</span>
              <span>25° (flexível)</span>
            </div>
          </div>
          
          {/* AGC: IoU Threshold */}
          <div className="mb-3">
            <label className="text-xs text-gray-400">
              🔀 IoU (remoção sobreposição): {(yoloOptions.iouThreshold * 100).toFixed(0)}%
            </label>
            <input
              type="range"
              min="0.3"
              max="0.8"
              step="0.05"
              value={yoloOptions.iouThreshold}
              onChange={(e) => setYoloOptions(prev => ({ ...prev, iouThreshold: parseFloat(e.target.value) }))}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-[10px] text-gray-500">
              <span>30% (menos merge)</span>
              <span>80% (mais merge)</span>
            </div>
          </div>
          
          {/* AGC: Min Points */}
          <div className="mb-3">
            <label className="text-xs text-gray-400">
              📍 Mínimo de pontos: {yoloOptions.minPoints}
            </label>
            <input
              type="range"
              min="3"
              max="8"
              step="1"
              value={yoloOptions.minPoints}
              onChange={(e) => setYoloOptions(prev => ({ ...prev, minPoints: parseInt(e.target.value) }))}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-[10px] text-gray-500">
              <span>3 (triângulo)</span>
              <span>8 (octógono)</span>
            </div>
          </div>
          
          {/* AGC: NEW - Scan Area Settings */}
          <div className="mt-3 pt-3 border-t border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold text-orange-400">📍 ÁREA DE ESCANEAMENTO</h4>
              <label className="flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={yoloOptions.useClickArea !== false}
                  onChange={(e) => setYoloOptions(prev => ({ ...prev, useClickArea: e.target.checked }))}
                  className="w-3 h-3 accent-orange-500"
                />
                <span className="text-xs text-gray-400">Apenas ao redor do clique</span>
              </label>
            </div>
            
            {yoloOptions.useClickArea !== false && (
              <div className="mb-2">
                <label className="text-xs text-gray-400">
                  🔲 Raio de escaneamento: {yoloOptions.scanRadiusPixels || 400}px
                </label>
                <input
                  type="range"
                  min="200"
                  max="800"
                  step="50"
                  value={yoloOptions.scanRadiusPixels || 400}
                  onChange={(e) => setYoloOptions(prev => ({ ...prev, scanRadiusPixels: parseInt(e.target.value) }))}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-[10px] text-gray-500">
                  <span>200px (pequeno)</span>
                  <span>800px (grande)</span>
                </div>
              </div>
            )}
            
            <p className="text-[10px] text-orange-300/60">
              {yoloOptions.useClickArea !== false 
                ? '✅ Escaneando apenas área ao redor do clique (mais rápido)'
                : '📷 Escaneando imagem completa (mais campos, mais lento)'
              }
            </p>
          </div>
          
          {/* AGC: User-saved YOLO Presets */}
          {yoloPresets.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-700">
              <h4 className="text-xs font-semibold text-blue-400 mb-2">💾 MEUS PRESETS YOLO</h4>
              <div className="flex flex-wrap gap-1">
                {yoloPresets.map(preset => (
                  <div key={preset.id} className="flex items-center gap-1">
                    <button
                      onClick={() => {
                        const { id, name, desc, isUserPreset, createdAt, ...presetOptions } = preset
                        setYoloOptions({ ...DEFAULT_YOLO_OPTIONS, ...presetOptions })
                      }}
                      className="text-[10px] px-2 py-1.5 rounded bg-blue-700 hover:bg-blue-600 text-white"
                      title={`Criado: ${new Date(preset.createdAt).toLocaleString()}`}
                    >
                      {preset.name}
                    </button>
                    <button
                      onClick={() => deleteYoloPreset(preset.id)}
                      className="text-[10px] px-1 py-1.5 rounded bg-red-800 hover:bg-red-600 text-white"
                      title="Excluir preset"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* AGC: Save YOLO Preset Button */}
          <div className="mt-3 pt-3 border-t border-gray-700">
            <button
              onClick={() => setShowSaveYoloPresetModal(true)}
              className="w-full py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg flex items-center justify-center gap-2"
            >
              💾 Salvar Configurações YOLO
            </button>
          </div>
          
          {/* AGC: Save YOLO Preset Modal */}
          {showSaveYoloPresetModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[90]">
              <div className="bg-gray-800 rounded-lg p-4 w-[280px] mx-4">
                <h3 className="text-white font-bold mb-3">🎯 Salvar Preset YOLO</h3>
                <input
                  type="text"
                  value={newPresetName}
                  onChange={(e) => setNewPresetName(e.target.value)}
                  placeholder="Nome do preset..."
                  onKeyDown={(e) => {
                    // AGC: Android keyboard "Go" → save YOLO preset
                    if (e.key === 'Enter' && newPresetName.trim()) { e.preventDefault(); saveYoloPreset() }
                  }}
                  enterKeyHint="go"
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg mb-3 text-sm"
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => { setShowSaveYoloPresetModal(false); setNewPresetName(''); }}
                    className="flex-1 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg text-sm"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={saveYoloPreset}
                    disabled={!newPresetName.trim()}
                    className="flex-1 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-500 text-white rounded-lg text-sm"
                  >
                    Salvar
                  </button>
                </div>
              </div>
            </div>
          )}
          
          <p className="text-xs text-gray-500 mt-2">
            {phase === 'preview' && lastDetectionRequest 
              ? '⚡ Ajuste os sliders - atualização automática em 0.5s'
              : '⚡ Faça uma detecção primeiro, depois ajuste os parâmetros em tempo real'
            }
          </p>
        </div>
      )}

      {/* AGC: Map Container wrapper.
          - `relative` so absolute children (draw-mode indicator, overlays)
            anchor to this wrapper.
          - No flex sizing here — the MapContainer below uses an explicit
            viewport-relative height (60dvh) that does NOT depend on any parent
            flex chain. This is the reliable fix for the blank-map regression:
            when MainMenu's root uses `min-h-screen` (not `h-screen`), `h-full`
            on this page's root can resolve to 0, causing `height: 100%` on
            the MapContainer to also be 0 → Leaflet sees a 0px container and
            renders blank tiles. By using `60dvh` we bypass the entire
            parent-percentage chain. `MapResizer` inside MapContainer also
            calls `invalidateSize()` as a belt-and-suspenders guard. */}
      <div className={`relative rounded-none overflow-hidden ${sampleMode === 'draw' && phase === 'idle' ? 'ring-4 ring-orange-400 ring-inset' : ''}`}>
        {/* AGC: Draw Mode Indicator Overlay */}
        {sampleMode === 'draw' && phase === 'idle' && (
          <div className="absolute top-2 left-2 z-[1000] bg-orange-500 text-white px-3 py-1.5 rounded-lg shadow-lg flex items-center gap-2 animate-pulse">
            <span className="text-lg">✏️</span>
            <span className="text-sm font-medium">Clique e arraste para desenhar área</span>
          </div>
        )}
        {phase === 'drawing' && (
          <div className="absolute top-2 left-2 z-[1000] bg-orange-600 text-white px-3 py-1.5 rounded-lg shadow-lg flex items-center gap-2">
            <span className="text-lg">📐</span>
            <span className="text-sm font-medium">Desenhando... solte para confirmar</span>
          </div>
        )}
        {/* AGC: MapContainer uses an explicit dvh height so it NEVER depends on
            parent percentage resolution. 60dvh gives a large, usable map on all
            phone screens without pushing the preview panel fully off-screen.
            `minHeight: 350` is a px floor for very short screens / landscape.
            The MapResizer child also calls invalidateSize() after 50ms as an
            extra guard against late-measured container scenarios. */}
        <MapContainer
          center={defaultCenter}
          zoom={defaultZoom}
          className="w-full"
          style={{ height: '60dvh', minHeight: '350px' }}
        >
          <TileLayer
            url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
            attribution="&copy; Google Maps"
            maxZoom={21}
          />
          
          {/* AGC: MapResizer re-measures the container after flex layout paints */}
          <MapResizer />
          <MapImageCapture ref={mapRef}>
            {/* AGC: Conditionally use click or draw mode */}
            {sampleMode === 'click' ? (
              <MapClickHandler
                onMapClick={handleMapClick}
                isEnabled={phase === 'idle' || refinementMode === 'add-positive' || refinementMode === 'add-negative'}
              />
            ) : (
              <MapDrawHandler
                onDrawStart={handleDrawStart}
                onDrawMove={handleDrawMove}
                onDrawEnd={handleDrawEnd}
                isEnabled={phase === 'idle' || phase === 'drawing'}
                isDrawing={phase === 'drawing'}
              />
            )}
            <ZoomTracker onZoomChange={setCurrentZoom} />
          </MapImageCapture>
          
          {/* AGC: Show freeform polygon while drawing */}
          {drawPoints.length > 1 && phase === 'drawing' && (
            <Polygon
              positions={drawPoints.map(p => [p.lat, p.lng])}
              pathOptions={{
                color: '#f59e0b',
                fillColor: '#f59e0b',
                fillOpacity: 0.3,
                weight: 3,
                dashArray: '5, 5',
              }}
              className="sample-area-polygon"
            />
          )}
          
          {/* AGC: Show sample polygon after drawing (before detection clears it) */}
          {samplePolygon && samplePolygon.length > 2 && phase === 'detecting' && (
            <Polygon
              positions={samplePolygon.map(p => [p.lat, p.lng])}
              pathOptions={{
                color: '#f59e0b',
                fillColor: '#f59e0b',
                fillOpacity: 0.2,
                weight: 2,
              }}
              className="sample-area-polygon"
            />
          )}
          
          {/* AGC: Show ALL YOLO detected fields (clickable) */}
          {yoloFields.length > 0 && yoloFields.map((field, index) => (
            <Polygon
              key={field.id}
              positions={field.coordinates.map(p => [p.lat, p.lng])}
              pathOptions={{
                color: index === selectedYoloIndex ? '#10b981' : '#3b82f6',
                fillColor: index === selectedYoloIndex ? '#10b981' : '#3b82f6',
                fillOpacity: index === selectedYoloIndex ? 0.4 : 0.15,
                weight: index === selectedYoloIndex ? 3 : 2,
              }}
              eventHandlers={{
                click: () => {
                  console.log(`🎯 AGC: Selected YOLO field ${index + 1}/${yoloFields.length}`)
                  setSelectedYoloIndex(index)
                  // AGC: Update detectedLot with selected field
                  setDetectedLot({
                    detectionId: field.id,
                    coordinates: field.coordinates,
                    confidence: field.confidence / 100,
                    metrics: {
                      areaHectares: field.areaHa,
                      areaSquareMeters: field.areaHa * 10000,
                      perimeterMeters: calcPerimeterMeters(field.coordinates),
                      perimeterKilometers: calcPerimeterMeters(field.coordinates) / 1000,
                      // AGC: centroid as {lat,lng} object, averaged across vertices
                      centroid: field.coordinates.length > 0
                        ? {
                            lat: field.coordinates.reduce((s: number, c: [number, number]) => s + c[0], 0) / field.coordinates.length,
                            lng: field.coordinates.reduce((s: number, c: [number, number]) => s + c[1], 0) / field.coordinates.length,
                          }
                        : { lat: 0, lng: 0 },
                      boundingBox: { northEast: { lat: 0, lng: 0 }, southWest: { lat: 0, lng: 0 } },
                    },
                    metadata: {
                      modelUsed: 'yolo-grid',
                      processingTimeMs: 0,
                      detectedLotType: (field.class as 'crop' | 'pasture' | 'forest' | 'fallow' | 'unknown') || 'unknown',
                      aiNotes: `Campo ${index + 1} de ${yoloFields.length}`,
                    },
                  })
                },
              }}
            />
          ))}
          
          {/* AGC: Show detected polygon (non-YOLO methods) */}
          {yoloFields.length === 0 && detectedLot && detectedLot.coordinates?.length > 0 && (
            <Polygon
              positions={detectedLot.coordinates.map(p => [p.lat, p.lng])}
              pathOptions={{
                color: '#10b981',
                fillColor: '#10b981',
                fillOpacity: 0.3,
                weight: 3,
              }}
            />
          )}
          
          {/* AGC: SAM 2 refinement point markers */}
          {positivePoints.map((pt, i) => (
            <CircleMarker key={`pos-${i}`} center={[pt.lat, pt.lng]} radius={6}
              pathOptions={{ color: '#22c55e', fillColor: '#22c55e', fillOpacity: 0.8, weight: 2 }} />
          ))}
          {negativePoints.map((pt, i) => (
            <CircleMarker key={`neg-${i}`} center={[pt.lat, pt.lng]} radius={6}
              pathOptions={{ color: '#ef4444', fillColor: '#ef4444', fillOpacity: 0.8, weight: 2 }} />
          ))}

          {/* AGC: Show click point marker - ONLY during detection (not while capturing) */}
          {/* AGC: Removed animate-ping as it interferes with screenshot capture */}
          {clickPoint && phase === 'preview' && (
            <div
              className="absolute z-[1000] pointer-events-none"
              style={{
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            >
              <div className="w-4 h-4 bg-emerald-500 rounded-full border-2 border-white shadow-lg" />
            </div>
          )}
        </MapContainer>

        {/* AGC: Zoom Level Indicator - Top Right Corner */}
        <div className="absolute top-3 right-3 z-[1000]">
          <div className="bg-black/70 text-white text-xs font-mono px-2 py-1 rounded shadow">
            Zoom: {currentZoom}
          </div>
        </div>

        {/* AGC: My Location button - sits directly UNDER the Zoom indicator at
            top-right so it doesn't overlap the zoom label. Right-aligned with
            the same `right-3` as the zoom indicator, with `top-12` giving a
            small gap below it. */}
        <button
          onClick={handleGoToMyLocation}
          disabled={isGettingLocation || phase === 'detecting'}
          className="absolute top-12 right-3 z-[1000] w-10 h-10 bg-white dark:bg-gray-800 rounded-full shadow-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Minha localização"
        >
          {isGettingLocation ? (
            <span className="animate-spin text-base">⏳</span>
          ) : (
            <Locate className="w-5 h-5 text-[#0B5F4A]" />
          )}
        </button>

        {/* AGC: SAM 2 Refinement Controls — green (+) and red (-) buttons */}
        {phase === 'preview' && canRefine && refinementMode === 'none' && (
          <div className="absolute bottom-24 right-3 flex flex-col gap-2 z-[1000]">
            <button
              onClick={() => setRefinementMode('add-positive')}
              className="w-12 h-12 rounded-full bg-green-500 text-white shadow-lg flex items-center justify-center text-2xl font-bold active:bg-green-600"
              title="Incluir área"
            >
              +
            </button>
            <button
              onClick={() => setRefinementMode('add-negative')}
              className="w-12 h-12 rounded-full bg-red-500 text-white shadow-lg flex items-center justify-center text-2xl font-bold active:bg-red-600"
              title="Excluir área"
            >
              −
            </button>
            {refinementRound > 0 && (
              <span className="text-xs text-center text-white bg-black/50 rounded px-2 py-1">
                {refinementRound}/3
              </span>
            )}
          </div>
        )}

        {/* AGC: Refinement mode indicator */}
        {(refinementMode === 'add-positive' || refinementMode === 'add-negative') && (
          <div className="absolute top-14 left-1/2 -translate-x-1/2 z-[1000]">
            <div className={`px-4 py-2 rounded-full shadow-lg text-sm font-medium flex items-center gap-2 ${
              refinementMode === 'add-positive'
                ? 'bg-green-500 text-white'
                : 'bg-red-500 text-white'
            }`}>
              <span>{refinementMode === 'add-positive' ? '+ Toque na área para incluir' : '− Toque na área para excluir'}</span>
              <button
                onClick={() => setRefinementMode('none')}
                className="ml-1 w-5 h-5 rounded-full bg-white/30 flex items-center justify-center text-xs"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* AGC: YOLO field navigator overlay - bottom-left of satellite map */}
        {phase === 'preview' && yoloFields.length > 1 && (
          <div className="absolute bottom-1 left-1 z-[1000] flex items-center gap-1">
            <button
              type="button"
              onClick={() => setSelectedYoloIndex(Math.max(0, selectedYoloIndex - 1))}
              disabled={selectedYoloIndex === 0}
              className="w-7 h-7 flex items-center justify-center rounded bg-black/60 text-white disabled:opacity-40"
              aria-label="Anterior"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-2 py-1 rounded bg-black/60 text-white text-xs font-medium whitespace-nowrap">
              Talhão {selectedYoloIndex + 1} de {yoloFields.length}
            </span>
            <button
              type="button"
              onClick={() => setSelectedYoloIndex(Math.min(yoloFields.length - 1, selectedYoloIndex + 1))}
              disabled={selectedYoloIndex === yoloFields.length - 1}
              className="w-7 h-7 flex items-center justify-center rounded bg-black/60 text-white disabled:opacity-40"
              aria-label="Próximo"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            {/* AGC: Area of selected YOLO field */}
            <span className="px-2 py-1 rounded bg-black/60 text-white text-xs font-medium whitespace-nowrap">
              {formatArea(yoloFields[selectedYoloIndex]?.areaHa ?? 0)}
            </span>
          </div>
        )}

        {/* AGC: Idle State - Instructions Overlay — bottom-safe-4 lifts the card clear of the
            AGC: Android nav bar area so the description text isn't clipped on edge-to-edge devices. */}
        {phase === 'idle' && (
          <div className="absolute bottom-safe-4 left-4 right-4 z-[1000]">
            <div className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-emerald-500">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <SparklesIcon size="md" animated className="text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Toque para detectar</h3>
                  <p className="text-sm text-gray-600 mt-0.5">
                    Toque dentro do lote que deseja mapear. A IA irá detectar os limites automaticamente.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* AGC: Error State — same bottom-safe-4 lift as the idle card so the retry button stays on screen. */}
        {phase === 'error' && (
          <div className="absolute bottom-safe-4 left-4 right-4 z-[1000]">
            <div className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-red-500">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <X className="w-5 h-5 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">Erro na detecção</h3>
                  <p className="text-sm text-gray-600 mt-0.5">{errorMessage}</p>
                  <button
                    onClick={handleRetry}
                    className="mt-3 flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-200"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Tentar novamente
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* AGC: Preview Panel - Show detected lot details */}
      {phase === 'preview' && detectedLot && (
        <div
          ref={previewPanelRef}
          className="bg-white border-t border-gray-200 shadow-lg z-20"
        >
          {/* AGC: Main Info */}
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <SparklesIcon size="md" className="text-emerald-600" />
                <h3 className="font-semibold text-gray-900">Lote detectado</h3>
              </div>
              {/* AGC: Show detection method badge */}
              <div className="flex items-center gap-2">
                {detectionInfo?.method === 'color' && (
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
                    <Palette className="w-3 h-3" />
                    Cor (Grátis)
                  </span>
                )}
                {detectionInfo?.method === 'sam' && (
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full flex items-center gap-1">
                    <Zap className="w-3 h-3" />
                    SAM
                  </span>
                )}
                {detectionInfo?.usedFallback && (
                  <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                    Fallback
                  </span>
                )}
                {detectionInfo?.method === 'yolo-grid' && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    YOLO
                  </span>
                )}
                {!detectionInfo?.method?.includes('color') && !detectionInfo?.method?.includes('sam') && !detectionInfo?.method?.includes('yolo') && (
                  <AIProviderBadge provider={detectedLot.metadata.modelUsed.includes('claude') ? 'anthropic' : 'openai'} />
                )}
              </div>
            </div>
            
            {/* AGC: YOLO name input (navigation is on map overlay) */}
            {yoloFields.length > 0 && (
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-flex items-center px-2.5 py-1 rounded-lg bg-blue-100 text-blue-800 text-xs font-medium whitespace-nowrap">
                  Talhão {selectedYoloIndex + 1}/{yoloFields.length}
                </span>
                <input
                  type="text"
                  value={yoloFieldNames[selectedYoloIndex] ?? `Talhão ${selectedYoloIndex + 1}`}
                  onChange={(e) => {
                    const next = [...yoloFieldNames]
                    next[selectedYoloIndex] = e.target.value
                    setYoloFieldNames(next)
                  }}
                  onKeyDown={(e) => {
                    // AGC: Android keyboard "Go" / desktop Enter → confirm the detected lot
                    if (e.key === 'Enter') { e.preventDefault(); handleConfirm() }
                  }}
                  enterKeyHint="go"
                  placeholder="Nome do talhão"
                  className="flex-1 min-w-[100px] px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
            )}

            {/* AGC: Area + Perimeter */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="bg-emerald-50 rounded-lg px-3 py-2">
                <p className="text-xs text-emerald-600 font-medium">Área</p>
                <p className="text-base font-bold text-emerald-700">
                  {formatArea(detectedLot.metrics.areaHectares)}
                </p>
              </div>
              <div className="bg-blue-50 rounded-lg px-3 py-2">
                <p className="text-xs text-blue-600 font-medium">Perímetro</p>
                <p className="text-base font-bold text-blue-700">
                  {formatPerimeter(detectedLot.metrics.perimeterMeters)}
                </p>
              </div>
            </div>

            {/* AGC: Confidence bar */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-xs text-gray-500">Confiança</span>
                <span className="text-xs font-medium text-gray-700">
                  {Math.round(detectedLot.confidence * 100)}%
                </span>
              </div>
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full"
                  style={{ width: `${detectedLot.confidence * 100}%` }}
                />
              </div>
            </div>

            {/* AGC: Expandable details (non-YOLO only) */}
            {!(yoloFields.length > 0) && (
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="flex items-center justify-between w-full py-2 text-sm text-gray-600"
              >
                <span>Detalhes da detecção</span>
                {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            )}
            
            {showDetails && !(yoloFields.length > 0) && (
              <div className="pt-2 space-y-2 text-sm">
                {/* AGC: Detection method info */}
                <p className="text-gray-600">
                  <span className="font-medium">Método:</span>{' '}
                  {detectionInfo?.method === 'color' && 'Detecção por cor (grátis)'}
                  {detectionInfo?.method === 'sam' && 'SAM (Segment Anything)'}
                  {detectionInfo?.method === 'hybrid' && 'Híbrido (cor + SAM)'}
                  {!detectionInfo?.method && detectedLot.metadata.modelUsed}
                </p>
                {detectionInfo?.usedFallback && (
                  <p className="text-amber-600">
                    <span className="font-medium">⚠️ Fallback usado:</span> Cor teve baixa confiança, SAM foi utilizado
                  </p>
                )}
                {(detectedLot.metadata as any).usedEdgeFallback && (
                  <p className="text-blue-600">
                    <span className="font-medium">🔲 Detecção de bordas:</span> {(detectedLot.metadata as any).edgeFallbackMethod || 'Canny'} foi usado para melhorar precisão
                  </p>
                )}
                {detectedLot.metadata.detectedLotType && detectedLot.metadata.detectedLotType !== 'unknown' && (
                  <p className="text-gray-600">
                    <span className="font-medium">Tipo:</span> {detectedLot.metadata.detectedLotType}
                  </p>
                )}
                {detectedLot.metadata.detectedCropType && (
                  <p className="text-gray-600">
                    <span className="font-medium">Cultura:</span> {detectedLot.metadata.detectedCropType}
                  </p>
                )}
                {detectedLot.metadata.aiNotes && (
                  <p className="text-gray-600">
                    <span className="font-medium">Notas:</span> {detectedLot.metadata.aiNotes}
                  </p>
                )}
                <p className="text-gray-500 text-xs">
                  Processado em {detectionInfo?.processingTimeMs || detectedLot.metadata.processingTimeMs}ms
                </p>
              </div>
            )}
          </div>

          {/* AGC: Action Buttons */}
          <div ref={previewButtonsRef} className="flex gap-3 p-4 pt-0">
            <button
              onClick={handleRetry}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 rounded-xl font-medium text-gray-700 hover:bg-gray-200 transition-colors"
            >
              <RotateCcw className="w-5 h-5" />
              Tentar novamente
            </button>
            <button
              onClick={handleConfirm}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-emerald-500 rounded-xl font-medium text-white hover:bg-emerald-600 transition-colors"
            >
              <Check className="w-5 h-5" />
              Confirmar
            </button>
          </div>
          {/* AGC: Spacer to push buttons above the fixed bottom nav bar + mic button */}
          <div className="h-20" style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 8px)' }} />
        </div>
      )}

      {/* AGC: Loading Overlay */}
      {phase === 'detecting' && (
        <AIDetectionLoading phase="analyzing" onCancel={handleRetry} />
      )}
    </div>
  )
}

export default AIOneClickLot

